import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "../../components/Container";
import CustomSelect from "../../components/CustomSelect";
import DeskOption from "../../components/DeskOption";
import {
  useGetLocationsQuery,
  useLazyGetFloorsQuery,
  useLazyGetReportsV1Query,
  useLazyGetDoorGraphsQuery,
} from "../../redux/slices/api.slice";
import {
  Button,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import { BsInfoCircleFill } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import { desksSelectionType } from "../../interfaces/reports";
import usePermissioned from "../../hooks/usePermissioned";

type DayPeak = {
  time: string;
  occupancyPct: number;
  peakConcurrent?: number;
  totalDesks?: number;
  isNoData?: boolean;
};

type DayPeakGroup = {
  day: string;
  peaks: DayPeak[];
};

type PeakOccupancyRecord = {
  date: string;
  hour: number;
  occupancyPct: number;
  peakConcurrent: number;
  totalDesks: number;
};

type PeakOccupancyFloorPayload = {
  floorID: string;
  records: PeakOccupancyRecord[];
};

type PeakCsvRow = {
  day: string;
  hour: string;
  peakOccupancyPct: number;
  peakConcurrentDesks: number;
  totalDesks: number;
  rank: string;
};
// graph info key
type GraphInfoKey =
  | "peak"
  | "hourly"
  | "floorHour"
  | "frequency"
  | "dow"
  | "session"
  | "dailyUtilisation"
  | "deskTurnover"
  | "doorTraffic";

type OccupancyDisplayMode = "percentage" | "desks";

type HeatmapRow = {
  day: string;
  values: {
    timestamp: string;
    occupiedDesks: number;
    usedDeskTime?: number;
    availableDeskTime?: number;
    avgDesksUsed?: number;
    totalDesks?: number;
  }[];
};

const occupancyDisplayOptions = [
  { label: "%", value: "percentage" },
  { label: "Desks", value: "desks" },
];

type DailyFrequencyPoint = {
  day: string;
  usedPct: number;
  usedDesks: number;
  totalDesks?: number;
};

type DayOfWeekPoint = {
  day: string;
  avgUsagePct: number;
};

type DowBucketPayload = {
  day?: string;
  dayName?: string;
  weekday?: string;
  label?: string;
  percentage?: number;
  avgUsagePct?: number;
};

type DowComparisonPayload = {
  floorID?: string;
  buckets?: DowBucketPayload[];
  days?: DowBucketPayload[];
  weekdays?: DowBucketPayload[];
};

type DowCsvRow = {
  day: string;
  avgUsagePct: number;
};

type SessionLengthBucket = {
  label: string;
  pct: number;
  color: string;
  midpointMinutes: number;
  count?: number;
};

type AvgSessionBucketPayload = {
  label: string;
  count: number;
  percentage: number;
};

type AvgSessionPayload = {
  floorID: string;
  avgMinutes: number;
  totalDesks: number;
  buckets: AvgSessionBucketPayload[];
};

type HourlyBucketPayload = {
  hour: string;
  usedDeskTime: number;
  availableDeskTime: number;
  percentage: number;
};

type HourlyOccupancyPayload = {
  floorID: string;
  startTime: string;
  endTime: string;
  totalDesks: number;
  buckets: HourlyBucketPayload[];
};

type HourlyFloorEntry = {
  floorID: string;
  startTime: string;
  endTime: string;
  totalDesks: number;
  buckets: HourlyBucketPayload[];
};

type HourlyFloorPayload = {
  floors: HourlyFloorEntry[];
};

type HourlyCsvRow = {
  day: string;
  hour: string;
  occupancyPct: number;
};

type FrequencyDayPayload = {
  date: string;
  percentage: number;
  usedDesks: number;
  totalDesks: number;
};

type DailyFrequencyPayload = {
  floorID: string;
  startTime: string;
  endTime: string;
  days: FrequencyDayPayload[];
};

type DailyUtilisationDayPayload = {
  date: string;
  usedDeskMinutes: number;
  availableDeskMinutes: number;
  percentage: number;
};

type DailyUtilisationPayload = {
  floorID: string;
  startTime: string;
  endTime: string;
  totalDesks: number;
  days: DailyUtilisationDayPayload[];
};

type DailyFrequencyCsvRow = {
  day: string;
  usedPct: number;
  usedDesks: number;
  totalDesks: number;
};

type SessionCsvRow = {
  bucket: string;
  usedPct: number;
  count: number;
  avgMinutes: number;
  totalDesks: number;
};

type DailyUtilisationPoint = {
  day: string;
  utilisedPct: number;
  idlePct: number;
  totalDesks: number;
  businessHours: number;
};

type TurnoverDayValue = {
  day: string;
  sessions: number;
  occupiedMinutes: number;
};

type TurnoverDeskRow = {
  desk: string;
  values: TurnoverDayValue[];
};

type TurnoverApiDeskEntry = {
  deskID: string;
  turnover: number;
  occupiedMinutes: number;
  utilisationPct: number;
};

type TurnoverApiDayEntry = {
  date: string;
  desks: TurnoverApiDeskEntry[];
};

type TurnoverApiPayload = {
  floorID: string;
  startTime: string;
  endTime: string;
  days: TurnoverApiDayEntry[];
};

type TurnoverDeskSortOrder = "default" | "ascending" | "descending";

type DoorTrafficBucketPayload = {
  start: string;
  end: string;
  in: number;
  out: number;
  net: number;
};

type DoorTrafficPayload = {
  floorID: string;
  sensorIDs: string[];
  timeStart: string;
  timeEnd: string;
  period: string;
  granularity: string;
  totalIn: number;
  totalOut: number;
  net: number;
  buckets: DoorTrafficBucketPayload[];
};

type DoorTrafficPoint = {
  day: string;
  in: number;
  out: number;
};

/** Overlay accent: utilisation on Daily Frequency, frequency on Daily Utilisation */
const REPORTS_OVERLAY_BLUE = "#3B82F6";

const ReportsNew = () => {
  const DEFAULT_END_HOUR = 18;
  /** Set to true to show the Floor x Hour Heatmap card again. */
  const SHOW_FLOOR_HOUR_HEATMAP = false;
  const [locationID, setLocationID] = useState("");
  const { isPermissioned: canViewDoors } = usePermissioned("R", "doors");
  const [role, setRole] = useState<string[]>([]);

  useEffect(() => {
    setRole(JSON.parse(window.localStorage.getItem("role")) || []);
  }, []);
  // the door traffic card is restricted to DIL Admin and Environment
  // Manager only — the same roles that hold the "doors" read permission
  const canViewDoorTraffic =
    canViewDoors &&
    role.some(
      (r) => r === "DO-DILAdmin" || r === "DO-EnvironmentManager",
    );
  const [specModal, setSpecModal] = useState(false);
  const [sessionSpecModal, setSessionSpecModal] = useState(false);
  const [hourlySpecModal, setHourlySpecModal] = useState(false);
  const [frequencySpecModal, setFrequencySpecModal] = useState(false);
  const [dowSpecModal, setDowSpecModal] = useState(false);
  const [hourlyDesksModal, setHourlyDesksModal] = useState(false);
  const [sessionDesksModal, setSessionDesksModal] = useState(false);
  const [frequencyDesksModal, setFrequencyDesksModal] = useState(false);
  const [infoGraph, setInfoGraph] = useState<GraphInfoKey | null>(null);
  const [selectedChart, setSelectedChart] = useState<
    "peak" | "hourly" | "frequency" | "dow" | "session" | "dailyUtilisation" | "deskTurnover" | "doorTraffic" | null
  >(null);
  const { data: allLocations, isFetching: isFetchinglocations } =
    useGetLocationsQuery("");
  const [
    getFloors,
    { data: allFloors, isFetching: isFetchingFloors, isError: errorFloors },
  ] = useLazyGetFloorsQuery();
  const [getReportsV1, { isFetching: isFetchingPeakOccupancy }] =
    useLazyGetReportsV1Query();
  const [getDoorGraphs] = useLazyGetDoorGraphsQuery();
  const [floors, setFloors] = useState<{ label: string; value: string }[]>([]);
  const [selectedFloorIDs, setSelectedFloorIDs] = useState<string[]>([]);
  const [selectedSessionFloorID, setSelectedSessionFloorID] = useState("");
  const [sessionDesksSelection, setSessionDeskSelection] =
    useState<desksSelectionType | null>(null);
  const [sessionDeskMap, setSessionDeskMap] = useState<Record<string, string[]>>(
    {},
  );
  const [sessionDeskRange, setSessionDeskRange] = useState<Record<string, string[]>>(
    {},
  );
  const [selectedHourlyFloorID, setSelectedHourlyFloorID] = useState("");
  const [hourlyDesksSelection, setHourlyDeskSelection] =
    useState<desksSelectionType | null>(null);
  const [hourlyDeskMap, setHourlyDeskMap] = useState<Record<string, string[]>>({});
  const [hourlyDeskRange, setHourlyDeskRange] = useState<Record<string, string[]>>(
    {},
  );
  const [selectedFrequencyFloorID, setSelectedFrequencyFloorID] = useState("");
  const [frequencyDesksSelection, setFrequencyDeskSelection] =
    useState<desksSelectionType | null>(null);
  const [frequencyDeskMap, setFrequencyDeskMap] = useState<Record<string, string[]>>(
    {},
  );
  const [frequencyDeskRange, setFrequencyDeskRange] = useState<
    Record<string, string[]>
  >({});
  const [selectedDowFloorID, setSelectedDowFloorID] = useState("");
  const [dowDesksSelection, setDowDeskSelection] = useState<desksSelectionType | null>(
    null,
  );
  const [dowDeskMap, setDowDeskMap] = useState<Record<string, string[]>>({});
  const [dowDeskRange, setDowDeskRange] = useState<Record<string, string[]>>({});
  const [sessionBucketsData, setSessionBucketsData] = useState<
    SessionLengthBucket[] | null
  >(null);
  const [sessionAvgMinutes, setSessionAvgMinutes] = useState<number | null>(null);
  const [sessionTotalDesks, setSessionTotalDesks] = useState<number | null>(null);
  const [hourlyHeatmapData, setHourlyHeatmapData] = useState<HeatmapRow[] | null>(
    null,
  );
  const [hourlyOccupancyDisplayMode, setHourlyOccupancyDisplayMode] =
    useState<OccupancyDisplayMode>("percentage");
  const [peakOccupancyDisplayMode, setPeakOccupancyDisplayMode] =
    useState<OccupancyDisplayMode>("percentage");
  const [hourlyCsvRows, setHourlyCsvRows] = useState<HourlyCsvRow[]>([]);
  const [dailyFrequencyData, setDailyFrequencyData] = useState<
    DailyFrequencyPoint[] | null
  >(null);
  const [dowComparisonData, setDowComparisonData] = useState<DayOfWeekPoint[] | null>(
    null,
  );
  const [frequencyRequestError, setFrequencyRequestError] = useState<string | null>(
    null,
  );
  const [dowRequestError, setDowRequestError] = useState<string | null>(null);
  const [shouldScrollToExpanded, setShouldScrollToExpanded] = useState(false);
  const expandedChartRef = useRef<HTMLDivElement | null>(null);
  const peakChartScrollRef = useRef<HTMLDivElement | null>(null);
  const peakChartDragRef = useRef({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  });

  const initialEndTime = useMemo(() => {
    const date = new Date();
    date.setHours(DEFAULT_END_HOUR, 0, 0, 0);
    return date;
  }, [DEFAULT_END_HOUR]);

  /** 10 calendar days inclusive (start 08:00 through end DEFAULT_END_HOUR). DOW uses its own 7-day clamp when its modal opens. */
  const initialStartTime = useMemo(() => {
    const date = new Date(initialEndTime);
    date.setDate(date.getDate() - 9);
    date.setHours(8, 0, 0, 0);
    return date;
  }, [initialEndTime]);
  const [startTime, setStartTime] = useState<Date>(initialStartTime);
  const [endTime, setEndTime] = useState<Date>(initialEndTime);
  const [error, setError] = useState<string | null>(null);
  const [hourlyExportModal, setHourlyExportModal] = useState(false);
  const [hourlyExportStartTime, setHourlyExportStartTime] =
    useState<Date>(initialStartTime);
  const [hourlyExportEndTime, setHourlyExportEndTime] = useState<Date>(initialEndTime);
  const [hourlyExportError, setHourlyExportError] = useState<string | null>(null);
  const [isExportingHourlyCsv, setIsExportingHourlyCsv] = useState(false);
  const [peakExportModal, setPeakExportModal] = useState(false);
  const [peakExportStartTime, setPeakExportStartTime] = useState<Date>(initialStartTime);
  const [peakExportEndTime, setPeakExportEndTime] = useState<Date>(initialEndTime);
  const [peakExportError, setPeakExportError] = useState<string | null>(null);
  const [isExportingPeakCsv, setIsExportingPeakCsv] = useState(false);
  const [sessionExportModal, setSessionExportModal] = useState(false);
  const [sessionExportStartTime, setSessionExportStartTime] =
    useState<Date>(initialStartTime);
  const [sessionExportEndTime, setSessionExportEndTime] =
    useState<Date>(initialEndTime);
  const [sessionExportError, setSessionExportError] = useState<string | null>(null);
  const [isExportingSessionCsv, setIsExportingSessionCsv] = useState(false);
  const [dowExportModal, setDowExportModal] = useState(false);
  const [dowExportStartTime, setDowExportStartTime] = useState<Date>(initialStartTime);
  const [dowExportEndTime, setDowExportEndTime] = useState<Date>(initialEndTime);
  const [dowExportError, setDowExportError] = useState<string | null>(null);
  const [isExportingDowCsv, setIsExportingDowCsv] = useState(false);
  const [frequencyExportModal, setFrequencyExportModal] = useState(false);
  const [frequencyExportStartTime, setFrequencyExportStartTime] =
    useState<Date>(initialStartTime);
  const [frequencyExportEndTime, setFrequencyExportEndTime] =
    useState<Date>(initialEndTime);
  const [frequencyExportError, setFrequencyExportError] = useState<string | null>(
    null,
  );
  const [isExportingFrequencyCsv, setIsExportingFrequencyCsv] = useState(false);
  const [, setRequestError] = useState<string | null>(null);
  const [utilisationSpecModal, setUtilisationSpecModal] = useState(false);
  const [utilisationDesksModal, setUtilisationDesksModal] = useState(false);
  const [utilisationChartMode, setUtilisationChartMode] = useState<"bar" | "line">("bar");
  const [isExportingUtilisationCsv, setIsExportingUtilisationCsv] = useState(false);
  const [isExportingTurnoverCsv, setIsExportingTurnoverCsv] = useState(false);
  const [selectedUtilisationFloorID, setSelectedUtilisationFloorID] = useState("");
  const [utilisationDesksSelection, setUtilisationDeskSelection] =
    useState<desksSelectionType | null>(null);
  const [utilisationDeskMap, setUtilisationDeskMap] = useState<Record<string, string[]>>({});
  const [utilisationDeskRange, setUtilisationDeskRange] = useState<Record<string, string[]>>({});
  const [utilisationRequestError, setUtilisationRequestError] = useState<string | null>(null);
  const [dailyUtilisationApiData, setDailyUtilisationApiData] = useState<
    DailyUtilisationPoint[] | null
  >(null);

  const [showUtilisationOverlay, setShowUtilisationOverlay] = useState(false);
  const [frequencyUtilisationOverlay, setFrequencyUtilisationOverlay] = useState<DailyUtilisationPoint[] | null>(null);
  const [isFetchingFreqUtilOverlay, setIsFetchingFreqUtilOverlay] = useState(false);

  const [showFrequencyOverlay, setShowFrequencyOverlay] = useState(false);
  const [utilisationFrequencyOverlay, setUtilisationFrequencyOverlay] = useState<DailyFrequencyPoint[] | null>(null);
  const [isFetchingUtilFreqOverlay, setIsFetchingUtilFreqOverlay] = useState(false);

  const [turnoverSpecModal, setTurnoverSpecModal] = useState(false);
  const [turnoverDesksModal, setTurnoverDesksModal] = useState(false);
  const [selectedTurnoverFloorID, setSelectedTurnoverFloorID] = useState("");
  const [turnoverDesksSelection, setTurnoverDeskSelection] = useState<desksSelectionType | null>(null);
  const [turnoverDeskMap, setTurnoverDeskMap] = useState<Record<string, string[]>>({});
  const [turnoverDeskRange, setTurnoverDeskRange] = useState<Record<string, string[]>>({});
  const [turnoverRequestError, setTurnoverRequestError] = useState<string | null>(null);
  const [turnoverApiData, setTurnoverApiData] = useState<TurnoverDeskRow[] | null>(null);
  const [turnoverDeskSortOrder, setTurnoverDeskSortOrder] =
    useState<TurnoverDeskSortOrder>("default");
  const [doorTrafficSpecModal, setDoorTrafficSpecModal] = useState(false);
  const [selectedDoorTrafficFloorID, setSelectedDoorTrafficFloorID] = useState("");
  const [doorTrafficRequestError, setDoorTrafficRequestError] = useState<string | null>(null);
  const [doorTrafficData, setDoorTrafficData] = useState<DoorTrafficPoint[] | null>(null);
  const [hourlyViewMode, setHourlyViewMode] = useState<"days" | "floors">("days");
  const [selectedHourlyFloorIDs, setSelectedHourlyFloorIDs] = useState<string[]>([]);

  const locations = useMemo(() => {
    return allLocations?.map((loc) => ({ label: loc.name, value: loc.id })) || [];
  }, [allLocations]);

  useEffect(() => {
    if (locationID) {
      setSelectedFloorIDs([]);
      setSelectedSessionFloorID("");
      setSessionDeskSelection(null);
      setSessionDeskMap({});
      setSessionDeskRange({});
      setSelectedHourlyFloorID("");
      setSelectedHourlyFloorIDs([]);
      setSelectedFrequencyFloorID("");
      setSelectedDowFloorID("");
      setFrequencyDeskSelection(null);
      setFrequencyDeskMap({});
      setFrequencyDeskRange({});
      setDowDeskSelection(null);
      setDowDeskMap({});
      setDowDeskRange({});
      setSelectedUtilisationFloorID("");
      setUtilisationDeskSelection(null);
      setUtilisationDeskMap({});
      setUtilisationDeskRange({});
      setSelectedTurnoverFloorID("");
      setTurnoverDeskSelection(null);
      setTurnoverDeskMap({});
      setTurnoverDeskRange({});
      getFloors(locationID);
      return;
    }
    setFloors([]);
    setSelectedFloorIDs([]);
    setSelectedSessionFloorID("");
    setSessionDeskSelection(null);
    setSessionDeskMap({});
    setSessionDeskRange({});
    setSelectedHourlyFloorID("");
    setSelectedHourlyFloorIDs([]);
    setSelectedFrequencyFloorID("");
    setSelectedDowFloorID("");
    setFrequencyDeskSelection(null);
    setFrequencyDeskMap({});
    setFrequencyDeskRange({});
    setDowDeskSelection(null);
    setDowDeskMap({});
    setDowDeskRange({});
    setHourlyDeskSelection(null);
    setHourlyDeskMap({});
    setHourlyDeskRange({});
    setSelectedUtilisationFloorID("");
    setUtilisationDeskSelection(null);
    setUtilisationDeskMap({});
    setUtilisationDeskRange({});
    setSelectedTurnoverFloorID("");
    setTurnoverDeskSelection(null);
    setTurnoverDeskMap({});
    setTurnoverDeskRange({});
  }, [getFloors, locationID]);

  useEffect(() => {
    if (!selectedSessionFloorID) {
      setSessionDeskSelection(null);
      setSessionDeskMap({});
      setSessionDeskRange({});
      return;
    }

    setSessionDeskSelection((prev) => ({
      [selectedSessionFloorID]: {
        deskOption: prev?.[selectedSessionFloorID]?.deskOption || "",
        startDeskID: prev?.[selectedSessionFloorID]?.startDeskID || "",
        endDeskID: prev?.[selectedSessionFloorID]?.endDeskID || "",
        singleDeskId: prev?.[selectedSessionFloorID]?.singleDeskId || "",
      },
    }));
    setSessionDeskMap((prev) => ({
      [selectedSessionFloorID]: prev?.[selectedSessionFloorID] || [],
    }));
    setSessionDeskRange((prev) => ({
      [selectedSessionFloorID]: prev?.[selectedSessionFloorID] || [],
    }));
  }, [selectedSessionFloorID]);

  useEffect(() => {
    if (hourlyViewMode === "floors") return;
    if (!selectedHourlyFloorID) {
      setHourlyDeskSelection(null);
      setHourlyDeskMap({});
      setHourlyDeskRange({});
      return;
    }

    setHourlyDeskSelection((prev) => {
      const nextSelection: desksSelectionType = {};
      nextSelection[selectedHourlyFloorID] = {
        deskOption: prev?.[selectedHourlyFloorID]?.deskOption || "",
        startDeskID: prev?.[selectedHourlyFloorID]?.startDeskID || "",
        endDeskID: prev?.[selectedHourlyFloorID]?.endDeskID || "",
        singleDeskId: prev?.[selectedHourlyFloorID]?.singleDeskId || "",
      };
      return nextSelection;
    });
    setHourlyDeskMap((prev) => {
      const nextMap: Record<string, string[]> = {};
      nextMap[selectedHourlyFloorID] = prev?.[selectedHourlyFloorID] || [];
      return nextMap;
    });
    setHourlyDeskRange((prev) => {
      const nextRange: Record<string, string[]> = {};
      nextRange[selectedHourlyFloorID] = prev?.[selectedHourlyFloorID] || [];
      return nextRange;
    });
  }, [selectedHourlyFloorID, hourlyViewMode]);

  useEffect(() => {
    if (hourlyViewMode !== "floors") return;
    if (selectedHourlyFloorIDs.length === 0) {
      setHourlyDeskSelection(null);
      setHourlyDeskMap({});
      setHourlyDeskRange({});
      return;
    }
    setHourlyDeskSelection((prev) => {
      const next: desksSelectionType = {};
      selectedHourlyFloorIDs.forEach((fid) => {
        next[fid] = {
          deskOption: prev?.[fid]?.deskOption || "",
          startDeskID: prev?.[fid]?.startDeskID || "",
          endDeskID: prev?.[fid]?.endDeskID || "",
          singleDeskId: prev?.[fid]?.singleDeskId || "",
        };
      });
      return next;
    });
    setHourlyDeskMap((prev) => {
      const next: Record<string, string[]> = {};
      selectedHourlyFloorIDs.forEach((fid) => { next[fid] = prev?.[fid] || []; });
      return next;
    });
    setHourlyDeskRange((prev) => {
      const next: Record<string, string[]> = {};
      selectedHourlyFloorIDs.forEach((fid) => { next[fid] = prev?.[fid] || []; });
      return next;
    });
  }, [selectedHourlyFloorIDs, hourlyViewMode]);

  useEffect(() => {
    if (!selectedFrequencyFloorID) {
      setFrequencyDeskSelection(null);
      setFrequencyDeskMap({});
      setFrequencyDeskRange({});
      return;
    }

    setFrequencyDeskSelection((prev) => ({
      [selectedFrequencyFloorID]: {
        deskOption: prev?.[selectedFrequencyFloorID]?.deskOption || "",
        startDeskID: prev?.[selectedFrequencyFloorID]?.startDeskID || "",
        endDeskID: prev?.[selectedFrequencyFloorID]?.endDeskID || "",
        singleDeskId: prev?.[selectedFrequencyFloorID]?.singleDeskId || "",
      },
    }));
    setFrequencyDeskMap((prev) => ({
      [selectedFrequencyFloorID]: prev?.[selectedFrequencyFloorID] || [],
    }));
    setFrequencyDeskRange((prev) => ({
      [selectedFrequencyFloorID]: prev?.[selectedFrequencyFloorID] || [],
    }));
  }, [selectedFrequencyFloorID]);

  useEffect(() => {
    if (!selectedDowFloorID) {
      setDowDeskSelection(null);
      setDowDeskMap({});
      setDowDeskRange({});
      return;
    }

    setDowDeskSelection((prev) => ({
      [selectedDowFloorID]: {
        deskOption: prev?.[selectedDowFloorID]?.deskOption || "",
        startDeskID: prev?.[selectedDowFloorID]?.startDeskID || "",
        endDeskID: prev?.[selectedDowFloorID]?.endDeskID || "",
        singleDeskId: prev?.[selectedDowFloorID]?.singleDeskId || "",
      },
    }));
    setDowDeskMap((prev) => ({
      [selectedDowFloorID]: prev?.[selectedDowFloorID] || [],
    }));
    setDowDeskRange((prev) => ({
      [selectedDowFloorID]: prev?.[selectedDowFloorID] || [],
    }));
  }, [selectedDowFloorID]);

  useEffect(() => {
    if (!selectedUtilisationFloorID) {
      setUtilisationDeskSelection(null);
      setUtilisationDeskMap({});
      setUtilisationDeskRange({});
      return;
    }

    setUtilisationDeskSelection((prev) => ({
      [selectedUtilisationFloorID]: {
        deskOption: prev?.[selectedUtilisationFloorID]?.deskOption || "",
        startDeskID: prev?.[selectedUtilisationFloorID]?.startDeskID || "",
        endDeskID: prev?.[selectedUtilisationFloorID]?.endDeskID || "",
        singleDeskId: prev?.[selectedUtilisationFloorID]?.singleDeskId || "",
      },
    }));
    setUtilisationDeskMap((prev) => ({
      [selectedUtilisationFloorID]: prev?.[selectedUtilisationFloorID] || [],
    }));
    setUtilisationDeskRange((prev) => ({
      [selectedUtilisationFloorID]: prev?.[selectedUtilisationFloorID] || [],
    }));
  }, [selectedUtilisationFloorID]);

  useEffect(() => {
    if (!selectedTurnoverFloorID) {
      setTurnoverDeskSelection(null);
      setTurnoverDeskMap({});
      setTurnoverDeskRange({});
      return;
    }
    setTurnoverDeskSelection((prev) => ({
      [selectedTurnoverFloorID]: {
        deskOption: prev?.[selectedTurnoverFloorID]?.deskOption || "",
        startDeskID: prev?.[selectedTurnoverFloorID]?.startDeskID || "",
        endDeskID: prev?.[selectedTurnoverFloorID]?.endDeskID || "",
        singleDeskId: prev?.[selectedTurnoverFloorID]?.singleDeskId || "",
      },
    }));
    setTurnoverDeskMap((prev) => ({
      [selectedTurnoverFloorID]: prev?.[selectedTurnoverFloorID] || [],
    }));
    setTurnoverDeskRange((prev) => ({
      [selectedTurnoverFloorID]: prev?.[selectedTurnoverFloorID] || [],
    }));
  }, [selectedTurnoverFloorID]);

  useEffect(() => {
    const updated =
      allFloors?.map((floor) => ({ label: floor.name, value: floor.id })) || [];
    setFloors(errorFloors ? [] : updated);
  }, [allFloors, errorFloors]);

  useEffect(() => {
    if (!selectedChart || !shouldScrollToExpanded) return;
    expandedChartRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setShouldScrollToExpanded(false);
  }, [selectedChart, shouldScrollToExpanded]);

  useEffect(() => {
    if (window.location.hash !== "#door-traffic") return;
    const frame = requestAnimationFrame(() => {
      document.getElementById("door-traffic-card")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!selectedChart) return;

    if (selectedChart !== "peak") {
      setPeakOccupancyByFloor({});
      setPeakFloorOrder([]);
      setSelectedPeakFloorID("");
    }

    if (selectedChart !== "hourly") {
      setHourlyHeatmapData(null);
      setHourlyCsvRows([]);
    }

    if (selectedChart !== "frequency") {
      setDailyFrequencyData(null);
    }

    if (selectedChart !== "session") {
      setSessionBucketsData(null);
      setSessionAvgMinutes(null);
      setSessionTotalDesks(null);
    }

    if (selectedChart !== "dow") {
      setDowComparisonData(null);
    }

    if (selectedChart !== "doorTraffic") {
      setDoorTrafficData(null);
    }
  }, [selectedChart]);

  const handleCloseExpandedChart = () => {
    if (selectedChart === "peak") {
      setPeakOccupancyByFloor({});
      setPeakFloorOrder([]);
      setSelectedPeakFloorID("");
    } else if (selectedChart === "hourly") {
      setHourlyHeatmapData(null);
      setHourlyCsvRows([]);
    } else if (selectedChart === "frequency") {
      setDailyFrequencyData(null);
    } else if (selectedChart === "session") {
      setSessionBucketsData(null);
      setSessionAvgMinutes(null);
      setSessionTotalDesks(null);
    } else if (selectedChart === "dow") {
      setDowComparisonData(null);
    } else if (selectedChart === "dailyUtilisation") {
      setDailyUtilisationApiData(null);
    } else if (selectedChart === "deskTurnover") {
      setTurnoverApiData(null);
    } else if (selectedChart === "doorTraffic") {
      setDoorTrafficData(null);
    }
    setSelectedChart(null);
  };

  useEffect(() => {
    if (frequencySpecModal) {
      setFrequencyRequestError(null);
    }
  }, [frequencySpecModal]);

  useEffect(() => {
    if (turnoverSpecModal) {
      setTurnoverRequestError(null);
    }
  }, [turnoverSpecModal]);

  useEffect(() => {
    if (!dowSpecModal) return;
    setEndTime((prev) => {
      const end = new Date(prev);
      end.setHours(18, 0, 0, 0);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(8, 0, 0, 0);
      setStartTime(start);
      setError(null);
      return end;
    });
  }, [dowSpecModal]);

  useEffect(() => {
    if (!hourlyExportModal) return;
    setHourlyExportStartTime(new Date(startTime));
    setHourlyExportEndTime(new Date(endTime));
    setHourlyExportError(null);
  }, [hourlyExportModal, startTime, endTime]);

  useEffect(() => {
    if (!peakExportModal) return;
    setPeakExportStartTime(new Date(startTime));
    setPeakExportEndTime(new Date(endTime));
    setPeakExportError(null);
  }, [peakExportModal, startTime, endTime]);

  useEffect(() => {
    if (!sessionExportModal) return;
    setSessionExportStartTime(new Date(startTime));
    setSessionExportEndTime(new Date(endTime));
    setSessionExportError(null);
  }, [sessionExportModal, startTime, endTime]);

  useEffect(() => {
    if (!dowExportModal) return;
    setDowExportEndTime((prev) => {
      const end = new Date(prev);
      end.setHours(18, 0, 0, 0);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(8, 0, 0, 0);
      setDowExportStartTime(start);
      setDowExportError(null);
      return end;
    });
  }, [dowExportModal]);

  useEffect(() => {
    if (!frequencyExportModal) return;
    setFrequencyExportStartTime(new Date(startTime));
    setFrequencyExportEndTime(new Date(endTime));
    setFrequencyExportError(null);
  }, [frequencyExportModal, startTime, endTime]);

  const validateTimes = (start: Date, end: Date) => {
    const startMs = start.getTime();
    const endMs = end.getTime();
    const maxRangeDays = specModal || sessionSpecModal ? 30 : 10;
    const maxRangeMs = maxRangeDays * 24 * 60 * 60 * 1000;

    if (startMs > endMs) {
      setError("Start date & hour cannot be greater than End date & hour");
      return;
    }

    if (dowSpecModal) {
      const startDateOnly = new Date(start);
      startDateOnly.setHours(0, 0, 0, 0);
      const endDateOnly = new Date(end);
      endDateOnly.setHours(0, 0, 0, 0);
      const daysInclusive =
        Math.floor((endDateOnly.getTime() - startDateOnly.getTime()) / (24 * 60 * 60 * 1000)) +
        1;
      if (daysInclusive !== 7) {
        setError("Day-of-week comparison requires exactly 7 days.");
        return;
      }

      const startHourOnly = start.getHours();
      const endHourOnly = end.getHours();
      if (endHourOnly <= startHourOnly) {
        setError("End time hour must be after start time hour.");
        return;
      }
    }

    if (endMs - startMs > maxRangeMs) {
      setError(`Date range cannot exceed ${maxRangeDays} days`);
      return;
    }

    setError(null);
  };

  const handleStartDateChange = (date: Date | null) => {
    if (!date) return;
    setStartTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateTimes(updated, endTime);
      return updated;
    });
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date) return;
    setEndTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateTimes(startTime, updated);
      return updated;
    });
  };

  const parseTimeWithFixedMinutes = (timeString: string, prevDate: Date) => {
    const [hours] = timeString.split(":").map(Number);
    const updated = new Date(prevDate);
    updated.setHours(hours, 0, 0, 0);
    return updated;
  };

  const handleStartHourChange = (timeString: string) => {
    setStartTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateTimes(updated, endTime);
      return updated;
    });
  };

  const handleEndHourChange = (timeString: string) => {
    setEndTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateTimes(startTime, updated);
      return updated;
    });
  };

  const validateHourlyExportTimes = (start: Date, end: Date) => {
    const startMs = start.getTime();
    const endMs = end.getTime();
    const maxExportRangeMs = 30 * 24 * 60 * 60 * 1000;

    if (startMs > endMs) {
      setHourlyExportError("Start date & hour cannot be greater than End date & hour");
      return;
    }

    if (endMs - startMs > maxExportRangeMs) {
      setHourlyExportError("Export date range cannot exceed 30 days");
      return;
    }

    setHourlyExportError(null);
  };

  const handleHourlyExportStartDateChange = (date: Date | null) => {
    if (!date) return;
    setHourlyExportStartTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateHourlyExportTimes(updated, hourlyExportEndTime);
      return updated;
    });
  };

  const handleHourlyExportEndDateChange = (date: Date | null) => {
    if (!date) return;
    setHourlyExportEndTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateHourlyExportTimes(hourlyExportStartTime, updated);
      return updated;
    });
  };

  const handleHourlyExportStartHourChange = (timeString: string) => {
    setHourlyExportStartTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateHourlyExportTimes(updated, hourlyExportEndTime);
      return updated;
    });
  };

  const handleHourlyExportEndHourChange = (timeString: string) => {
    setHourlyExportEndTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateHourlyExportTimes(hourlyExportStartTime, updated);
      return updated;
    });
  };

  const validatePeakExportTimes = (start: Date, end: Date) => {
    const startMs = start.getTime();
    const endMs = end.getTime();
    const maxExportRangeMs = 30 * 24 * 60 * 60 * 1000;

    if (startMs > endMs) {
      setPeakExportError("Start date & hour cannot be greater than End date & hour");
      return;
    }

    if (endMs - startMs > maxExportRangeMs) {
      setPeakExportError("Export date range cannot exceed 30 days");
      return;
    }

    setPeakExportError(null);
  };

  const handlePeakExportStartDateChange = (date: Date | null) => {
    if (!date) return;
    setPeakExportStartTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validatePeakExportTimes(updated, peakExportEndTime);
      return updated;
    });
  };

  const handlePeakExportEndDateChange = (date: Date | null) => {
    if (!date) return;
    setPeakExportEndTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validatePeakExportTimes(peakExportStartTime, updated);
      return updated;
    });
  };

  const handlePeakExportStartHourChange = (timeString: string) => {
    setPeakExportStartTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validatePeakExportTimes(updated, peakExportEndTime);
      return updated;
    });
  };

  const handlePeakExportEndHourChange = (timeString: string) => {
    setPeakExportEndTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validatePeakExportTimes(peakExportStartTime, updated);
      return updated;
    });
  };

  const validateSessionExportTimes = (start: Date, end: Date) => {
    const startMs = start.getTime();
    const endMs = end.getTime();
    const maxExportRangeMs = 30 * 24 * 60 * 60 * 1000;

    if (startMs > endMs) {
      setSessionExportError("Start date & hour cannot be greater than End date & hour");
      return;
    }

    if (endMs - startMs > maxExportRangeMs) {
      setSessionExportError("Export date range cannot exceed 30 days");
      return;
    }

    setSessionExportError(null);
  };

  const handleSessionExportStartDateChange = (date: Date | null) => {
    if (!date) return;
    setSessionExportStartTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateSessionExportTimes(updated, sessionExportEndTime);
      return updated;
    });
  };

  const handleSessionExportEndDateChange = (date: Date | null) => {
    if (!date) return;
    setSessionExportEndTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateSessionExportTimes(sessionExportStartTime, updated);
      return updated;
    });
  };

  const handleSessionExportStartHourChange = (timeString: string) => {
    setSessionExportStartTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateSessionExportTimes(updated, sessionExportEndTime);
      return updated;
    });
  };

  const handleSessionExportEndHourChange = (timeString: string) => {
    setSessionExportEndTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateSessionExportTimes(sessionExportStartTime, updated);
      return updated;
    });
  };

  const validateDowExportTimes = (start: Date, end: Date) => {
    const startMs = start.getTime();
    const endMs = end.getTime();

    if (startMs > endMs) {
      setDowExportError("Start date & hour cannot be greater than End date & hour");
      return;
    }

    const startDateOnly = new Date(start);
    startDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);
    const daysInclusive =
      Math.floor((endDateOnly.getTime() - startDateOnly.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    if (daysInclusive !== 7) {
      setDowExportError("Day-of-week comparison requires exactly 7 days.");
      return;
    }

    if (end.getHours() <= start.getHours()) {
      setDowExportError("End time hour must be after start time hour.");
      return;
    }

    setDowExportError(null);
  };

  const handleDowExportStartDateChange = (date: Date | null) => {
    if (!date) return;
    setDowExportStartTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateDowExportTimes(updated, dowExportEndTime);
      return updated;
    });
  };

  const handleDowExportEndDateChange = (date: Date | null) => {
    if (!date) return;
    setDowExportEndTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateDowExportTimes(dowExportStartTime, updated);
      return updated;
    });
  };

  const handleDowExportStartHourChange = (timeString: string) => {
    setDowExportStartTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateDowExportTimes(updated, dowExportEndTime);
      return updated;
    });
  };

  const handleDowExportEndHourChange = (timeString: string) => {
    setDowExportEndTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateDowExportTimes(dowExportStartTime, updated);
      return updated;
    });
  };

  const validateFrequencyExportTimes = (start: Date, end: Date) => {
    const startMs = start.getTime();
    const endMs = end.getTime();
    const maxExportRangeMs = 30 * 24 * 60 * 60 * 1000;

    if (startMs > endMs) {
      setFrequencyExportError("Start date & hour cannot be greater than End date & hour");
      return;
    }

    if (endMs - startMs > maxExportRangeMs) {
      setFrequencyExportError("Export date range cannot exceed 30 days");
      return;
    }

    setFrequencyExportError(null);
  };

  const handleFrequencyExportStartDateChange = (date: Date | null) => {
    if (!date) return;
    setFrequencyExportStartTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateFrequencyExportTimes(updated, frequencyExportEndTime);
      return updated;
    });
  };

  const handleFrequencyExportEndDateChange = (date: Date | null) => {
    if (!date) return;
    setFrequencyExportEndTime((prev) => {
      const updated = new Date(prev);
      updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      validateFrequencyExportTimes(frequencyExportStartTime, updated);
      return updated;
    });
  };

  const handleFrequencyExportStartHourChange = (timeString: string) => {
    setFrequencyExportStartTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateFrequencyExportTimes(updated, frequencyExportEndTime);
      return updated;
    });
  };

  const handleFrequencyExportEndHourChange = (timeString: string) => {
    setFrequencyExportEndTime((prev) => {
      const updated = parseTimeWithFixedMinutes(timeString, prev);
      validateFrequencyExportTimes(frequencyExportStartTime, updated);
      return updated;
    });
  };

  const formatDateTimeForApi = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hour = pad(date.getHours());
    const minute = pad(date.getMinutes());
    const second = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hour}:${minute}:${second}+00:00`;
  };

  const formatCsvDay = (value: string) => {
    const [year, month, day] = value.split("-");
    if (!year || !month || !day) return value;
    const normalizedDay = day.padStart(2, "0");
    const normalizedMonth = month.padStart(2, "0");
    return `${normalizedDay}/${normalizedMonth}/${year}`;
  };

  const [peakOccupancyByFloor, setPeakOccupancyByFloor] = useState<
    Record<string, DayPeakGroup[]>
  >({});
  const [peakFloorOrder, setPeakFloorOrder] = useState<string[]>([]);
  const [selectedPeakFloorID, setSelectedPeakFloorID] = useState("");

  const buildPeakChartData = (records: PeakOccupancyRecord[]): DayPeakGroup[] => {
    const recordsByDate = new Map<string, PeakOccupancyRecord[]>();

    records.forEach((record) => {
      const current = recordsByDate.get(record.date) || [];
      current.push(record);
      recordsByDate.set(record.date, current);
    });

    return Array.from(recordsByDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, records]) => {
        const sortedTopThree = [...records]
          .sort((a, b) => b.occupancyPct - a.occupancyPct)
          .slice(0, 3)
          .map((record) => {
            const occupancyPct = Number(record.occupancyPct.toFixed(2));
            const peakConcurrent = Number(record.peakConcurrent ?? 0);
            const isNoData = occupancyPct <= 0 && peakConcurrent <= 0;
            return {
              time: `${String(record.hour).padStart(2, "0")}:00`,
              occupancyPct,
              peakConcurrent: record.peakConcurrent,
              totalDesks: record.totalDesks,
              isNoData,
            };
          });

        const dayLabel = new Date(`${date}T00:00:00Z`).toLocaleDateString(
          "en-US",
          {
            weekday: "short",
            day: "2-digit",
            month: "short",
            timeZone: "UTC",
          },
        );

        return { day: dayLabel, peaks: sortedTopThree };
      });
  };

  const handleApplyPeakOccupancyParams = async () => {
    try {
      setRequestError(null);
      const response = await getReportsV1({
        graph: "peak_occupancy",
        content: {
          floorIDs: selectedFloorIDs,
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap();

      const payload = (response as PeakOccupancyFloorPayload[]) || [];
      const floorSeries: Record<string, DayPeakGroup[]> = {};
      const floorOrder: string[] = [];

      payload.forEach((floorEntry) => {
        if (!floorEntry.floorID || floorEntry.records.length === 0) return;
        floorSeries[floorEntry.floorID] = buildPeakChartData(floorEntry.records);
        floorOrder.push(floorEntry.floorID);
      });

      if (floorOrder.length === 0) {
        setRequestError("No peak occupancy data found for selected filters.");
        return;
      }

      setPeakOccupancyByFloor(floorSeries);
      setPeakFloorOrder(floorOrder);
      setSelectedPeakFloorID(floorOrder[0]);
      setSpecModal(false);
      setSelectedChart("peak");
      setShouldScrollToExpanded(true);
    } catch (err) {
      setRequestError("Failed to fetch peak occupancy data. Please try again.");
    }
  };

  const fetchAverageSessionLength = async ({
    floorID,
    closeModal,
  }: {
    floorID: string;
    closeModal: boolean;
  }) => {
    try {
      setRequestError(null);
      const response = (await getReportsV1({
        graph: "avg_session_length",
        content: {
          floorID,
          deskRange: sessionDeskMap[floorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as AvgSessionPayload;

      const sessionBucketMeta: Record<
        string,
        { color: string; midpointMinutes: number }
      > = {
        "< 30 min": { color: "#22C55E", midpointMinutes: 20 },
        "30-60 min": { color: "#9CD67A", midpointMinutes: 45 },
        "1-2 hrs": { color: "#F59E0B", midpointMinutes: 90 },
        "2-4 hrs": { color: "#F97316", midpointMinutes: 180 },
        "4+ hrs": { color: "#B42318", midpointMinutes: 300 },
      };

      const mappedBuckets: SessionLengthBucket[] = (response?.buckets || []).map(
        (bucket) => {
          const fallbackMidpoint = bucket.label === "4+ hrs" ? 300 : 60;
          return {
            label: bucket.label,
            pct: Number(bucket.percentage.toFixed(2)),
            count: bucket.count,
            color: sessionBucketMeta[bucket.label]?.color || "#98A2B3",
            midpointMinutes:
              sessionBucketMeta[bucket.label]?.midpointMinutes || fallbackMidpoint,
          };
        },
      );
      const nonZeroBuckets = mappedBuckets.filter(
        (bucket) => bucket.pct > 0 && (bucket.count ?? 0) > 0,
      );

      if (nonZeroBuckets.length === 0) {
        setRequestError("No average session length data found for selected filters.");
        return;
      }

      setSessionBucketsData(nonZeroBuckets);
      setSessionAvgMinutes(Math.round(response.avgMinutes || 0));
      setSessionTotalDesks(response.totalDesks ?? null);

      if (closeModal) {
        setSessionSpecModal(false);
      }
      setSelectedChart("session");
      setShouldScrollToExpanded(true);
    } catch (err) {
      setRequestError("Failed to fetch average session length data. Please try again.");
    }
  };

  const handleApplySessionParams = async () => {
    await fetchAverageSessionLength({
      floorID: selectedSessionFloorID,
      closeModal: true,
    });
  };

  const handleSessionFloorChange = async (value: string) => {
    setSelectedSessionFloorID(value);
    if (!value || selectedChart !== "session" || error !== null) return;
    await fetchAverageSessionLength({ floorID: value, closeModal: false });
  };

  const fetchHourlyOccupancy = async ({
    floorID,
    floorIDs,
    closeModal,
  }: {
    floorID?: string;
    floorIDs?: string[];
    closeModal: boolean;
  }) => {
    try {
      setRequestError(null);
      const isFloorsMode = floorIDs !== undefined;

      const rawResponse = (await getReportsV1({
        graph: "hourly_occupancy",
        content: isFloorsMode
          ? {
              floorIDs,
              deskRange: [],
              startTime: formatDateTimeForApi(startTime),
              endTime: formatDateTimeForApi(endTime),
            }
          : {
              floorID: floorID!,
              deskRange: hourlyDeskMap[floorID!] || [],
              startTime: formatDateTimeForApi(startTime),
              endTime: formatDateTimeForApi(endTime),
            },
      }).unwrap()) as HourlyOccupancyPayload | { payload: HourlyOccupancyPayload };

      if (isFloorsMode) {
        const floorsPayload: HourlyFloorPayload =
          ((rawResponse as unknown as { payload?: HourlyFloorPayload })?.payload) ||
          (rawResponse as unknown as HourlyFloorPayload);

        const transformedHeatmap: HeatmapRow[] = (floorsPayload.floors || []).map((floor) => {
          const floorLabel = floors.find((f) => f.value === floor.floorID)?.label || floor.floorID;
          const values = [...floor.buckets]
            .sort((a, b) => a.hour.localeCompare(b.hour))
            .map((bucket) => {
              const timePart = bucket.hour.split(" ")[1] || "";
              const avgDesksUsed =
                bucket.availableDeskTime > 0
                  ? (bucket.usedDeskTime / bucket.availableDeskTime) * floor.totalDesks
                  : 0;
              return {
                timestamp: timePart.slice(0, 5),
                occupiedDesks: Number(bucket.percentage.toFixed(2)),
                usedDeskTime: bucket.usedDeskTime,
                availableDeskTime: bucket.availableDeskTime,
                avgDesksUsed: Number(avgDesksUsed.toFixed(2)),
                totalDesks: floor.totalDesks,
              };
            });
          return { day: floorLabel, values };
        });

        const csvRows: HourlyCsvRow[] = (floorsPayload.floors || []).flatMap((floor) => {
          const floorLabel = floors.find((f) => f.value === floor.floorID)?.label || floor.floorID;
          return [...floor.buckets]
            .sort((a, b) => a.hour.localeCompare(b.hour))
            .map((bucket) => ({
              day: floorLabel,
              hour: bucket.hour.split(" ")[1] || bucket.hour,
              occupancyPct: Number(bucket.percentage ?? 0),
            }));
        });

        if (transformedHeatmap.length === 0) {
          setHourlyCsvRows([]);
          setRequestError("No hourly occupancy data found for selected filters.");
          return;
        }

        setHourlyHeatmapData(transformedHeatmap);
        setHourlyCsvRows(csvRows);
        if (closeModal) setHourlySpecModal(false);
        setSelectedChart("hourly");
        if (closeModal) setShouldScrollToExpanded(true);
        return;
      }

      const response =
        (rawResponse as { payload?: HourlyOccupancyPayload })?.payload ||
        (rawResponse as HourlyOccupancyPayload);
      const csvRows: HourlyCsvRow[] = [...(response?.buckets || [])]
        .sort((a, b) => a.hour.localeCompare(b.hour))
        .map((bucket) => {
          const [day, hour] = bucket.hour.split(" ");
          return {
            day: day ? formatCsvDay(day) : "",
            hour: hour || bucket.hour,
            occupancyPct: Number(bucket.percentage ?? 0),
          };
        });

      const groupedByDate = new Map<string, HeatmapRow["values"]>();
      (response?.buckets || []).forEach((bucket) => {
        const [datePart, timePart] = bucket.hour.split(" ");
        if (!datePart || !timePart) return;
        const existing = groupedByDate.get(datePart) || [];
        const avgDesksUsed =
          bucket.availableDeskTime > 0
            ? (bucket.usedDeskTime / bucket.availableDeskTime) *
              (response?.totalDesks || 0)
            : 0;
        existing.push({
          timestamp: timePart.slice(0, 5),
          occupiedDesks: Number(bucket.percentage.toFixed(2)),
          usedDeskTime: bucket.usedDeskTime,
          availableDeskTime: bucket.availableDeskTime,
          avgDesksUsed: Number(avgDesksUsed.toFixed(2)),
          totalDesks: response?.totalDesks || 0,
        });
        groupedByDate.set(datePart, existing);
      });

      const transformedHeatmap = Array.from(groupedByDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([datePart, values]) => ({
          day: new Date(`${datePart}T00:00:00Z`).toLocaleDateString("en-US", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            timeZone: "UTC",
          }),
          values: values.sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
        }));

      if (transformedHeatmap.length === 0) {
        setHourlyCsvRows([]);
        setRequestError("No hourly occupancy data found for selected filters.");
        return;
      }

      setHourlyHeatmapData(transformedHeatmap);
      setHourlyCsvRows(csvRows);

      if (closeModal) {
        setHourlySpecModal(false);
      }
      setSelectedChart("hourly");
      if (closeModal) {
        setShouldScrollToExpanded(true);
      }
    } catch (err) {
      setHourlyCsvRows([]);
      setRequestError("Failed to fetch hourly occupancy trend data. Please try again.");
    }
  };

  const handleApplyHourlyParams = async () => {
    if (hourlyViewMode === "floors") {
      await fetchHourlyOccupancy({ floorIDs: selectedHourlyFloorIDs, closeModal: true });
    } else {
      await fetchHourlyOccupancy({ floorID: selectedHourlyFloorID, closeModal: true });
    }
  };

  const handleHourlyFloorChange = async (value: string) => {
    setSelectedHourlyFloorID(value);
    if (!value || selectedChart !== "hourly" || error !== null || hourlyViewMode === "floors") return;
    await fetchHourlyOccupancy({ floorID: value, closeModal: false });
  };

  const handleHourlyViewModeChange = (mode: "days" | "floors") => {
    setHourlyViewMode(mode);
    setSelectedHourlyFloorIDs([]);
    setHourlyDeskSelection(null);
    setHourlyDeskMap({});
    setHourlyDeskRange({});
    if (mode === "floors") {
      setEndTime((prev) => {
        const synced = new Date(prev);
        synced.setFullYear(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
        return synced;
      });
    }
  };

  const handleFloorsModeDateChange = (date: Date | null) => {
    if (!date) return;
    const y = date.getFullYear(), mo = date.getMonth(), d = date.getDate();
    setStartTime((prev) => { const u = new Date(prev); u.setFullYear(y, mo, d); return u; });
    setEndTime((prev) => { const u = new Date(prev); u.setFullYear(y, mo, d); return u; });
    setError(null);
  };

  const exportHourlyCsv = (rowsToExport: HourlyCsvRow[]) => {
    if (rowsToExport.length === 0) return;
    const headers = [
      "Day",
      "Hour",
      "Occupancy %",
    ];
    const escapeCsvValue = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const rows = rowsToExport.map((row) => [
      row.day,
      row.hour,
      row.occupancyPct.toFixed(2),
    ]);
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "hourly-occupancy-trend.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportHourlyCsv = async () => {
    if (!selectedHourlyFloorID || hourlyExportError !== null) return;
    try {
      setIsExportingHourlyCsv(true);
      setHourlyExportError(null);
      const rawResponse = (await getReportsV1({
        graph: "hourly_occupancy",
        content: {
          floorID: selectedHourlyFloorID,
          deskRange: hourlyDeskMap[selectedHourlyFloorID] || [],
          startTime: formatDateTimeForApi(hourlyExportStartTime),
          endTime: formatDateTimeForApi(hourlyExportEndTime),
        },
      }).unwrap()) as HourlyOccupancyPayload | { payload: HourlyOccupancyPayload };
      const response =
        (rawResponse as { payload?: HourlyOccupancyPayload })?.payload ||
        (rawResponse as HourlyOccupancyPayload);
      const exportRows: HourlyCsvRow[] = [...(response?.buckets || [])]
        .sort((a, b) => a.hour.localeCompare(b.hour))
        .map((bucket) => {
          const [day, hour] = bucket.hour.split(" ");
          return {
            day: day ? formatCsvDay(day) : "",
            hour: hour || bucket.hour,
            occupancyPct: Number(bucket.percentage ?? 0),
          };
        });

      if (exportRows.length === 0) {
        setHourlyExportError("No hourly occupancy data found for selected filters.");
        return;
      }

      exportHourlyCsv(exportRows);
      setHourlyExportModal(false);
    } catch (err) {
      setHourlyExportError(
        "Failed to export hourly occupancy data. Please try again.",
      );
    } finally {
      setIsExportingHourlyCsv(false);
    }
  };

  const exportDailyFrequencyCsv = (rowsToExport: DailyFrequencyCsvRow[]) => {
    if (rowsToExport.length === 0) return;
    const headers = ["Day", "Used %", "Used desks", "Total desks"];
    const escapeCsvValue = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const rows = rowsToExport.map((row) => [
      row.day,
      row.usedPct.toFixed(2),
      row.usedDesks,
      row.totalDesks,
    ]);
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "daily-frequency-trend.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportDailyUtilisationCsv = (rows: DailyUtilisationPoint[]) => {
    if (rows.length === 0) return;
    const headers = ["Day", "Utilised %", "Total desks", "Business hours"];
    const escapeCsvValue = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) =>
        [row.day, row.utilisedPct.toFixed(2), row.totalDesks, row.businessHours]
          .map(escapeCsvValue)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "daily-utilisation.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportUtilisationCsv = async () => {
    if (!selectedUtilisationFloorID) return;
    try {
      setIsExportingUtilisationCsv(true);
      const rawResponse = (await getReportsV1({
        graph: "daily_utilisation",
        content: {
          floorID: selectedUtilisationFloorID,
          deskRange: utilisationDeskMap[selectedUtilisationFloorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as DailyUtilisationPayload | { payload: DailyUtilisationPayload };
      const response =
        (rawResponse as { payload?: DailyUtilisationPayload })?.payload ||
        (rawResponse as DailyUtilisationPayload);
      const totalDesks = Number(response?.totalDesks ?? 0);
      const rows: DailyUtilisationPoint[] = [...(response?.days || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day) => {
          const utilisedPct = Number(Number(day.percentage ?? 0).toFixed(2));
          const businessHours = totalDesks > 0
            ? Math.round(Number(day.availableDeskMinutes ?? 0) / totalDesks / 60)
            : 8;
          return {
            day: formatCsvDay(day.date),
            utilisedPct,
            idlePct: Number((100 - utilisedPct).toFixed(2)),
            totalDesks,
            businessHours,
          };
        });
      exportDailyUtilisationCsv(rows);
    } catch {
      // silent — no modal for this export
    } finally {
      setIsExportingUtilisationCsv(false);
    }
  };

  const handleExportDailyFrequencyCsv = async () => {
    if (!selectedFrequencyFloorID || frequencyExportError !== null) return;
    try {
      setIsExportingFrequencyCsv(true);
      setFrequencyExportError(null);
      const rawResponse = (await getReportsV1({
        graph: "frequency",
        content: {
          floorID: selectedFrequencyFloorID,
          deskRange: frequencyDeskMap[selectedFrequencyFloorID] || [],
          startTime: formatDateTimeForApi(frequencyExportStartTime),
          endTime: formatDateTimeForApi(frequencyExportEndTime),
        },
      }).unwrap()) as DailyFrequencyPayload | { payload: DailyFrequencyPayload };
      const response =
        (rawResponse as { payload?: DailyFrequencyPayload })?.payload ||
        (rawResponse as DailyFrequencyPayload);
      const exportRows: DailyFrequencyCsvRow[] = [...(response?.days || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day) => ({
          day: formatCsvDay(day.date),
          usedPct: Number(day.percentage ?? 0),
          usedDesks: Number(day.usedDesks ?? 0),
          totalDesks: Number(day.totalDesks ?? 0),
        }));

      if (exportRows.length === 0) {
        setFrequencyExportError("No daily frequency data found for selected filters.");
        return;
      }

      exportDailyFrequencyCsv(exportRows);
      setFrequencyExportModal(false);
    } catch (err) {
      setFrequencyExportError("Failed to export daily frequency data. Please try again.");
    } finally {
      setIsExportingFrequencyCsv(false);
    }
  };

  const exportDowCsv = (rowsToExport: DowCsvRow[]) => {
    if (rowsToExport.length === 0) return;
    const headers = ["Day", "Average Usage %"];
    const escapeCsvValue = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const rows = rowsToExport.map((row) => [row.day, row.avgUsagePct.toFixed(2)]);
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "day-of-week-comparison.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportDowCsv = async () => {
    if (!selectedDowFloorID || dowExportError !== null) return;
    try {
      setIsExportingDowCsv(true);
      setDowExportError(null);
      const rawResponse = (await getReportsV1({
        graph: "day_of_week_comparison",
        content: {
          floorID: selectedDowFloorID,
          deskRange: dowDeskMap[selectedDowFloorID] || [],
          startTime: formatDateTimeForApi(dowExportStartTime),
          endTime: formatDateTimeForApi(dowExportEndTime),
        },
      }).unwrap()) as DowComparisonPayload | { payload: DowComparisonPayload };
      const response =
        (rawResponse as { payload?: DowComparisonPayload })?.payload ||
        (rawResponse as DowComparisonPayload);

      const normalizeDayLabel = (value: string) => {
        const normalized = value.trim().toLowerCase();
        const map: Record<string, string> = {
          mon: "Mon",
          monday: "Mon",
          tue: "Tue",
          tues: "Tue",
          tuesday: "Tue",
          wed: "Wed",
          wednesday: "Wed",
          thu: "Thu",
          thur: "Thu",
          thurs: "Thu",
          thursday: "Thu",
          fri: "Fri",
          friday: "Fri",
          sat: "Sat",
          saturday: "Sat",
          sun: "Sun",
          sunday: "Sun",
        };
        return map[normalized] || "";
      };

      const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const directCandidates =
        response?.buckets || response?.days || response?.weekdays || [];
      const dynamicArrayCandidate = Object.values(
        (response as Record<string, unknown>) || {},
      ).find(
        (value) =>
          Array.isArray(value) &&
          value.some(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              ("percentage" in (item as Record<string, unknown>) ||
                "avgUsagePct" in (item as Record<string, unknown>)),
          ),
      );
      const normalizedArraySource = Array.isArray(dynamicArrayCandidate)
        ? dynamicArrayCandidate
        : directCandidates;

      const objectDaySource = Object.entries(
        (response as Record<string, unknown>) || {},
      )
        .map(([key, value]) => {
          const day = normalizeDayLabel(key);
          if (!day) return null;
          if (typeof value === "number") {
            return { day, avgUsagePct: value };
          }
          if (typeof value === "object" && value !== null) {
            const record = value as Record<string, unknown>;
            const pct = Number(record.percentage ?? record.avgUsagePct ?? NaN);
            if (Number.isFinite(pct)) {
              return { day, avgUsagePct: pct };
            }
          }
          return null;
        })
        .filter((item): item is { day: string; avgUsagePct: number } => item !== null);

      const mappedFromArray: DayOfWeekPoint[] = (normalizedArraySource as DowBucketPayload[])
        .map((item) => {
          const rawDay = String(
            item.dayName || item.day || item.weekday || item.label || "",
          );
          const day = normalizeDayLabel(rawDay);
          return {
            day,
            avgUsagePct: Number(item.percentage ?? item.avgUsagePct ?? 0),
          };
        })
        .filter((item) => item.day.length > 0);

      const mergedByDay = new Map<string, number>();
      [...mappedFromArray, ...objectDaySource].forEach((item) => {
        if (!item.day) return;
        mergedByDay.set(item.day, Number(item.avgUsagePct ?? 0));
      });

      const exportRows: DowCsvRow[] = Array.from(mergedByDay.entries())
        .map(([day, avgUsagePct]) => ({
          day,
          avgUsagePct: Number(avgUsagePct.toFixed(2)),
        }))
        .sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));

      if (exportRows.length === 0) {
        setDowExportError("No day-of-week comparison data found for selected filters.");
        return;
      }

      exportDowCsv(exportRows);
      setDowExportModal(false);
    } catch (err) {
      setDowExportError(
        "Failed to export day-of-week comparison data. Please try again.",
      );
    } finally {
      setIsExportingDowCsv(false);
    }
  };

  const exportPeakCsv = (rowsToExport: PeakCsvRow[]) => {
    if (rowsToExport.length === 0) return;
    const headers = [
      "Day",
      "Hour",
      "Peak Occupancy %",
      "Peak Concurrent Desks",
      "Total Desks",
      "Rank",
    ];
    const escapeCsvValue = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const rows = rowsToExport.map((row) => [
      row.day,
      row.hour,
      row.peakOccupancyPct.toFixed(2),
      row.peakConcurrentDesks,
      row.totalDesks,
      row.rank,
    ]);
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "peak-occupancy.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPeakCsv = async () => {
    if (!activePeakFloorID || peakExportError !== null) return;
    try {
      setIsExportingPeakCsv(true);
      setPeakExportError(null);
      const rawResponse = (await getReportsV1({
        graph: "peak_occupancy",
        content: {
          floorIDs: [activePeakFloorID],
          startTime: formatDateTimeForApi(peakExportStartTime),
          endTime: formatDateTimeForApi(peakExportEndTime),
        },
      }).unwrap()) as PeakOccupancyFloorPayload[] | { payload: PeakOccupancyFloorPayload[] };
      const response =
        (rawResponse as { payload?: PeakOccupancyFloorPayload[] })?.payload ||
        (rawResponse as PeakOccupancyFloorPayload[]);

      const floorPayload = (response || []).find(
        (item) => item.floorID === activePeakFloorID,
      );
      const records = floorPayload?.records || [];
      const recordsByDate = new Map<string, PeakOccupancyRecord[]>();
      records.forEach((record) => {
        const current = recordsByDate.get(record.date) || [];
        current.push(record);
        recordsByDate.set(record.date, current);
      });

      const exportRows: PeakCsvRow[] = Array.from(recordsByDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .flatMap(([date, dailyRecords]) => {
          const uniqueSortedPeaks = Array.from(
            new Set(dailyRecords.map((record) => Number(record.occupancyPct ?? 0))),
          ).sort((a, b) => b - a);
          return [...dailyRecords]
            .sort((a, b) => a.hour - b.hour)
            .map((record) => {
              const rankIndex = uniqueSortedPeaks.indexOf(
                Number(record.occupancyPct ?? 0),
              );
              return {
                day: formatCsvDay(date),
                hour: `${String(record.hour).padStart(2, "0")}:00`,
                peakOccupancyPct: Number(record.occupancyPct ?? 0),
                peakConcurrentDesks: Number(record.peakConcurrent ?? 0),
                totalDesks: Number(record.totalDesks ?? 0),
                rank: `Top ${rankIndex + 1}`,
              };
            });
        });

      if (exportRows.length === 0) {
        setPeakExportError("No peak occupancy data found for selected filters.");
        return;
      }

      exportPeakCsv(exportRows);
      setPeakExportModal(false);
    } catch (err) {
      setPeakExportError("Failed to export peak occupancy data. Please try again.");
    } finally {
      setIsExportingPeakCsv(false);
    }
  };

  const exportSessionCsv = (rowsToExport: SessionCsvRow[]) => {
    if (rowsToExport.length === 0) return;
    const headers = [
      "Bucket",
      "Used %",
      "Count",
      "Average Session Minutes",
      "Total Desks",
    ];
    const escapeCsvValue = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;
    const rows = rowsToExport.map((row) => [
      row.bucket,
      row.usedPct.toFixed(2),
      row.count,
      row.avgMinutes,
      row.totalDesks,
    ]);
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "average-session-length.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportSessionCsv = async () => {
    if (!selectedSessionFloorID || sessionExportError !== null) return;
    try {
      setIsExportingSessionCsv(true);
      setSessionExportError(null);
      const rawResponse = (await getReportsV1({
        graph: "avg_session_length",
        content: {
          floorID: selectedSessionFloorID,
          deskRange: sessionDeskMap[selectedSessionFloorID] || [],
          startTime: formatDateTimeForApi(sessionExportStartTime),
          endTime: formatDateTimeForApi(sessionExportEndTime),
        },
      }).unwrap()) as AvgSessionPayload | { payload: AvgSessionPayload };
      const response =
        (rawResponse as { payload?: AvgSessionPayload })?.payload ||
        (rawResponse as AvgSessionPayload);
      const exportRows: SessionCsvRow[] = [...(response?.buckets || [])].map(
        (bucket) => ({
          bucket: bucket.label,
          usedPct: Number(bucket.percentage ?? 0),
          count: Number(bucket.count ?? 0),
          avgMinutes: Number(response?.avgMinutes ?? 0),
          totalDesks: Number(response?.totalDesks ?? 0),
        }),
      );

      if (exportRows.length === 0) {
        setSessionExportError(
          "No average session length data found for selected filters.",
        );
        return;
      }

      exportSessionCsv(exportRows);
      setSessionExportModal(false);
    } catch (err) {
      setSessionExportError(
        "Failed to export average session length data. Please try again.",
      );
    } finally {
      setIsExportingSessionCsv(false);
    }
  };

  const handleExportTurnoverCsv = () => {
    if (!turnoverApiData || turnoverApiData.length === 0) return;
    try {
      setIsExportingTurnoverCsv(true);
      const days = turnoverApiData[0]?.values.map((v) => v.day) || [];
      const escapeCsvValue = (value: string | number) =>
        `"${String(value).replace(/"/g, '""')}"`;
      const headers = ["Desk", ...days.flatMap((d) => [`${d} Sessions`, `${d} Occupied (min)`])];
      const csvContent = [
        headers.map(escapeCsvValue).join(","),
        ...turnoverApiData.map((row) =>
          [
            row.desk,
            ...days.flatMap((day) => {
              const cell = row.values.find((v) => v.day === day);
              return [cell?.sessions ?? 0, cell?.occupiedMinutes ?? 0];
            }),
          ]
            .map(escapeCsvValue)
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "desk-turnover.csv");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingTurnoverCsv(false);
    }
  };

  const fetchDailyFrequency = async ({
    floorID,
    closeModal,
  }: {
    floorID: string;
    closeModal: boolean;
  }) => {
    try {
      setFrequencyRequestError(null);
      const rawResponse = (await getReportsV1({
        graph: "frequency",
        content: {
          floorID,
          deskRange: frequencyDeskMap[floorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as DailyFrequencyPayload | { payload: DailyFrequencyPayload };
      const response =
        (rawResponse as { payload?: DailyFrequencyPayload })?.payload ||
        (rawResponse as DailyFrequencyPayload);

      const mappedDays: DailyFrequencyPoint[] = [...(response?.days || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day) => ({
          day: new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-US", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            timeZone: "UTC",
          }),
          usedPct: Number(Number(day.percentage ?? 0).toFixed(2)),
          usedDesks: Number(day.usedDesks ?? 0),
          totalDesks: Number(day.totalDesks ?? 0),
        }));

      if (mappedDays.length === 0) {
        setFrequencyRequestError("No daily frequency data found for selected filters.");
        return;
      }

      setDailyFrequencyData(mappedDays);

      if (showUtilisationOverlay) {
        await fetchFrequencyUtilisationOverlay();
      } else {
        setFrequencyUtilisationOverlay(null);
      }

      if (closeModal) {
        setFrequencySpecModal(false);
      }
      setSelectedChart("frequency");
      if (closeModal) {
        setShouldScrollToExpanded(true);
      }
    } catch (err) {
      setFrequencyRequestError("Failed to fetch daily frequency data. Please try again.");
    }
  };

  const handleApplyDailyFrequencyParams = async () => {
    await fetchDailyFrequency({
      floorID: selectedFrequencyFloorID,
      closeModal: true,
    });
  };

  const handleFrequencyFloorChange = async (value: string) => {
    setSelectedFrequencyFloorID(value);
    if (!value || selectedChart !== "frequency" || error !== null) return;
    await fetchDailyFrequency({ floorID: value, closeModal: false });
  };

  const fetchDailyUtilisation = async ({
    floorID,
    closeModal,
  }: {
    floorID: string;
    closeModal: boolean;
  }) => {
    try {
      setUtilisationRequestError(null);
      const rawResponse = (await getReportsV1({
        graph: "daily_utilisation",
        content: {
          floorID,
          deskRange: utilisationDeskMap[floorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as DailyUtilisationPayload | { payload: DailyUtilisationPayload };
      const response =
        (rawResponse as { payload?: DailyUtilisationPayload })?.payload ||
        (rawResponse as DailyUtilisationPayload);

      const totalDesks = Number(response?.totalDesks ?? 0);
      const mappedDays: DailyUtilisationPoint[] = [...(response?.days || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day) => {
          const utilisedPct = Number(Number(day.percentage ?? 0).toFixed(2));
          const businessHours = totalDesks > 0
            ? Math.round(Number(day.availableDeskMinutes ?? 0) / totalDesks / 60)
            : 8;
          return {
            day: new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-US", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              timeZone: "UTC",
            }),
            utilisedPct,
            idlePct: Number((100 - utilisedPct).toFixed(2)),
            totalDesks,
            businessHours,
          };
        });

      if (mappedDays.length === 0) {
        setUtilisationRequestError("No daily utilisation data found for selected filters.");
        return;
      }

      setDailyUtilisationApiData(mappedDays);

      if (showFrequencyOverlay) {
        await fetchUtilisationFrequencyOverlay();
      } else {
        setUtilisationFrequencyOverlay(null);
      }

      if (closeModal) {
        setUtilisationSpecModal(false);
      }
      setSelectedChart("dailyUtilisation");
      if (closeModal) {
        setShouldScrollToExpanded(true);
      }
    } catch (err) {
      setUtilisationRequestError("Failed to fetch daily utilisation data. Please try again.");
    }
  };

  const handleApplyUtilisationParams = async () => {
    await fetchDailyUtilisation({
      floorID: selectedUtilisationFloorID,
      closeModal: true,
    });
  };

  const handleUtilisationFloorChange = async (value: string) => {
    setSelectedUtilisationFloorID(value);
    if (!value || selectedChart !== "dailyUtilisation" || error !== null) return;
    await fetchDailyUtilisation({ floorID: value, closeModal: false });
  };

  const fetchDeskTurnover = async ({ floorID, closeModal }: { floorID: string; closeModal: boolean }) => {
    try {
      setTurnoverRequestError(null);
      const rawResponse = (await getReportsV1({
        graph: "desk_turnover",
        content: {
          floorID,
          deskRange: turnoverDeskMap[floorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as TurnoverApiPayload | { payload: TurnoverApiPayload };
      const response =
        (rawResponse as { payload?: TurnoverApiPayload })?.payload ||
        (rawResponse as TurnoverApiPayload);

      const allDays = [...(response?.days || [])].sort((a, b) => a.date.localeCompare(b.date));
      const formattedDays = allDays.map((day) =>
        new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-US", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          timeZone: "UTC",
        })
      );

      const deskIDSet = new Set<string>();
      allDays.forEach((day) => day.desks.forEach((d) => deskIDSet.add(d.deskID)));
      const deskIDs = Array.from(deskIDSet).sort();

      const rows: TurnoverDeskRow[] = deskIDs.map((deskID) => ({
        desk: deskID,
        values: allDays.map((day, idx) => {
          const entry = day.desks.find((d) => d.deskID === deskID);
          return {
            day: formattedDays[idx],
            sessions: entry?.turnover ?? 0,
            occupiedMinutes: entry?.occupiedMinutes ?? 0,
          };
        }),
      }));

      if (rows.length === 0) {
        setTurnoverRequestError("No desk turnover data found for selected filters.");
        return;
      }

      setTurnoverApiData(rows);
      if (closeModal) setTurnoverSpecModal(false);
      setSelectedChart("deskTurnover");
      if (closeModal) setShouldScrollToExpanded(true);
    } catch {
      setTurnoverRequestError("Failed to fetch desk turnover data. Please try again.");
    }
  };

  const handleApplyTurnoverParams = async () => {
    await fetchDeskTurnover({ floorID: selectedTurnoverFloorID, closeModal: true });
  };

  const handleTurnoverFloorChange = async (value: string) => {
    setSelectedTurnoverFloorID(value);
    if (!value || selectedChart !== "deskTurnover" || error !== null) return;
    await fetchDeskTurnover({ floorID: value, closeModal: false });
  };

  const fetchDoorTraffic = async ({ floorID, closeModal }: { floorID: string; closeModal: boolean }) => {
    try {
      setDoorTrafficRequestError(null);
      const response = (await getDoorGraphs({
        floorID,
        granularity: "day",
        timeStart: formatDateTimeForApi(startTime),
        timeEnd: formatDateTimeForApi(endTime),
      }).unwrap()) as DoorTrafficPayload;

      if ((response?.sensorIDs?.length ?? 0) === 0) {
        setDoorTrafficRequestError("No door sensors assigned to this floor.");
        return;
      }

      const points: DoorTrafficPoint[] = [...(response?.buckets || [])]
        .sort((a, b) => a.start.localeCompare(b.start))
        .map((bucket) => ({
          // keep the bucket's calendar day regardless of the viewer's timezone
          day: new Date(`${bucket.start.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            timeZone: "UTC",
          }),
          in: bucket.in ?? 0,
          out: bucket.out ?? 0,
        }));

      if (points.length === 0) {
        setDoorTrafficRequestError("No door traffic data found for selected filters.");
        return;
      }

      setDoorTrafficData(points);
      if (closeModal) setDoorTrafficSpecModal(false);
      setSelectedChart("doorTraffic");
      if (closeModal) setShouldScrollToExpanded(true);
    } catch {
      setDoorTrafficRequestError("Failed to fetch door traffic data. Please try again.");
    }
  };

  const handleApplyDoorTrafficParams = async () => {
    await fetchDoorTraffic({ floorID: selectedDoorTrafficFloorID, closeModal: true });
  };

  const handleDoorTrafficFloorChange = async (value: string) => {
    setSelectedDoorTrafficFloorID(value);
    if (!value || selectedChart !== "doorTraffic" || error !== null) return;
    await fetchDoorTraffic({ floorID: value, closeModal: false });
  };

  const fetchFrequencyUtilisationOverlay = async () => {
    setIsFetchingFreqUtilOverlay(true);
    try {
      const rawResponse = (await getReportsV1({
        graph: "daily_utilisation",
        content: {
          floorID: selectedFrequencyFloorID,
          deskRange: frequencyDeskMap[selectedFrequencyFloorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as DailyUtilisationPayload | { payload: DailyUtilisationPayload };
      const response =
        (rawResponse as { payload?: DailyUtilisationPayload })?.payload ||
        (rawResponse as DailyUtilisationPayload);
      const totalDesks = Number(response?.totalDesks ?? 0);
      const mappedDays: DailyUtilisationPoint[] = [...(response?.days || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day) => {
          const utilisedPct = Number(Number(day.percentage ?? 0).toFixed(2));
          const businessHours = totalDesks > 0
            ? Math.round(Number(day.availableDeskMinutes ?? 0) / totalDesks / 60)
            : 8;
          return {
            day: new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-US", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              timeZone: "UTC",
            }),
            utilisedPct,
            idlePct: Number((100 - utilisedPct).toFixed(2)),
            totalDesks,
            businessHours,
          };
        });
      setFrequencyUtilisationOverlay(mappedDays.length > 0 ? mappedDays : null);
    } catch {
      setFrequencyUtilisationOverlay(null);
    } finally {
      setIsFetchingFreqUtilOverlay(false);
    }
  };

  const fetchUtilisationFrequencyOverlay = async () => {
    setIsFetchingUtilFreqOverlay(true);
    try {
      const rawResponse = (await getReportsV1({
        graph: "frequency",
        content: {
          floorID: selectedUtilisationFloorID,
          deskRange: utilisationDeskMap[selectedUtilisationFloorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as DailyFrequencyPayload | { payload: DailyFrequencyPayload };
      const response =
        (rawResponse as { payload?: DailyFrequencyPayload })?.payload ||
        (rawResponse as DailyFrequencyPayload);
      const mappedDays: DailyFrequencyPoint[] = [...(response?.days || [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day) => ({
          day: new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-US", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            timeZone: "UTC",
          }),
          usedPct: Number(Number(day.percentage ?? 0).toFixed(2)),
          usedDesks: Number(day.usedDesks ?? 0),
          totalDesks: Number(day.totalDesks ?? 0),
        }));
      setUtilisationFrequencyOverlay(mappedDays.length > 0 ? mappedDays : null);
    } catch {
      setUtilisationFrequencyOverlay(null);
    } finally {
      setIsFetchingUtilFreqOverlay(false);
    }
  };

  const handleToggleUtilisationOverlay = async () => {
    if (showUtilisationOverlay) {
      setShowUtilisationOverlay(false);
    } else {
      setShowUtilisationOverlay(true);
      if (!frequencyUtilisationOverlay) {
        await fetchFrequencyUtilisationOverlay();
      }
    }
  };

  const handleToggleFrequencyOverlay = async () => {
    if (showFrequencyOverlay) {
      setShowFrequencyOverlay(false);
    } else {
      setShowFrequencyOverlay(true);
      if (!utilisationFrequencyOverlay) {
        await fetchUtilisationFrequencyOverlay();
      }
    }
  };

  const fetchDowComparison = async ({
    floorID,
    closeModal,
  }: {
    floorID: string;
    closeModal: boolean;
  }) => {
    try {
      setDowRequestError(null);
      const rawResponse = (await getReportsV1({
        graph: "day_of_week_comparison",
        content: {
          floorID,
          deskRange: dowDeskMap[floorID] || [],
          startTime: formatDateTimeForApi(startTime),
          endTime: formatDateTimeForApi(endTime),
        },
      }).unwrap()) as DowComparisonPayload | { payload: DowComparisonPayload };
      const response =
        (rawResponse as { payload?: DowComparisonPayload })?.payload ||
        (rawResponse as DowComparisonPayload);

      const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const normalizeDayLabel = (value: string) => {
        const normalized = value.trim().toLowerCase();
        const map: Record<string, string> = {
          mon: "Mon",
          monday: "Mon",
          tue: "Tue",
          tues: "Tue",
          tuesday: "Tue",
          wed: "Wed",
          wednesday: "Wed",
          thu: "Thu",
          thur: "Thu",
          thurs: "Thu",
          thursday: "Thu",
          fri: "Fri",
          friday: "Fri",
          sat: "Sat",
          saturday: "Sat",
          sun: "Sun",
          sunday: "Sun",
        };
        return map[normalized] || "";
      };

      const directCandidates =
        response?.buckets || response?.days || response?.weekdays || [];
      const dynamicArrayCandidate = Object.values(
        (response as Record<string, unknown>) || {},
      ).find(
        (value) =>
          Array.isArray(value) &&
          value.some(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              ("percentage" in (item as Record<string, unknown>) ||
                "avgUsagePct" in (item as Record<string, unknown>)),
          ),
      );
      const normalizedArraySource = Array.isArray(dynamicArrayCandidate)
        ? dynamicArrayCandidate
        : directCandidates;

      const objectDaySource = Object.entries(
        (response as Record<string, unknown>) || {},
      )
        .map(([key, value]) => {
          const day = normalizeDayLabel(key);
          if (!day) return null;
          if (typeof value === "number") {
            return { day, avgUsagePct: value };
          }
          if (typeof value === "object" && value !== null) {
            const record = value as Record<string, unknown>;
            const pct = Number(record.percentage ?? record.avgUsagePct ?? NaN);
            if (Number.isFinite(pct)) {
              return { day, avgUsagePct: pct };
            }
          }
          return null;
        })
        .filter((item): item is { day: string; avgUsagePct: number } => item !== null);

      const mappedFromArray: DayOfWeekPoint[] = (normalizedArraySource as DowBucketPayload[])
        .map((item) => {
          const rawDay = String(
            item.dayName || item.day || item.weekday || item.label || "",
          );
          const day = normalizeDayLabel(rawDay);
          return {
            day,
            avgUsagePct: Number(item.percentage ?? item.avgUsagePct ?? 0),
          };
        })
        .filter((item) => item.day.length > 0);

      const mergedByDay = new Map<string, number>();
      [...mappedFromArray, ...objectDaySource].forEach((item) => {
        if (!item.day) return;
        mergedByDay.set(item.day, Number(item.avgUsagePct ?? 0));
      });

      const mappedDays: DayOfWeekPoint[] = Array.from(mergedByDay.entries())
        .map(([day, avgUsagePct]) => ({
          day,
          avgUsagePct: Number(avgUsagePct.toFixed(2)),
        }))
        .sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));

      if (mappedDays.length === 0) {
        setDowRequestError("No day-of-week comparison data found for selected filters.");
        return;
      }

      setDowComparisonData(mappedDays);
      if (closeModal) {
        setDowSpecModal(false);
      }
      setSelectedChart("dow");
      if (closeModal) {
        setShouldScrollToExpanded(true);
      }
    } catch (err) {
      setDowRequestError(
        "Failed to fetch day-of-week comparison data. Please try again.",
      );
    }
  };

  const handleApplyDowParams = async () => {
    await fetchDowComparison({
      floorID: selectedDowFloorID,
      closeModal: true,
    });
  };

  const handleDowFloorChange = async (value: string) => {
    setSelectedDowFloorID(value);
    if (!value || selectedChart !== "dow" || error !== null) return;
    await fetchDowComparison({ floorID: value, closeModal: false });
  };

  const handlePeakChartMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!peakChartScrollRef.current) return;
    peakChartDragRef.current = {
      isDragging: true,
      startX: event.pageX - peakChartScrollRef.current.offsetLeft,
      scrollLeft: peakChartScrollRef.current.scrollLeft,
    };
  };

  const handlePeakChartMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!peakChartDragRef.current.isDragging || !peakChartScrollRef.current) return;
    event.preventDefault();
    const x = event.pageX - peakChartScrollRef.current.offsetLeft;
    const walk = x - peakChartDragRef.current.startX;
    peakChartScrollRef.current.scrollLeft =
      peakChartDragRef.current.scrollLeft - walk;
  };

  const handlePeakChartMouseUp = () => {
    peakChartDragRef.current.isDragging = false;
  };

  // Example data shown by default until user chooses graph parameters.
  const peakOccupancyExample = useMemo<DayPeakGroup[]>(() => {
    const raw: DayPeakGroup[] = [
      {
        day: "Mon",
        peaks: [
          { time: "10:30", occupancyPct: 20 },
          { time: "13:00", occupancyPct: 35 },
          { time: "15:00", occupancyPct: 67 },
        ],
      },
      {
        day: "Tue",
        peaks: [
          { time: "09:45", occupancyPct: 28 },
          { time: "12:30", occupancyPct: 48 },
          { time: "15:30", occupancyPct: 74 },
        ],
      },
      {
        day: "Wed",
        peaks: [
          { time: "10:00", occupancyPct: 25 },
          { time: "12:45", occupancyPct: 51 },
          { time: "14:30", occupancyPct: 79 },
        ],
      },
      {
        day: "Thu",
        peaks: [
          { time: "09:30", occupancyPct: 30 },
          { time: "13:15", occupancyPct: 56 },
          { time: "16:00", occupancyPct: 83 },
        ],
      },
      {
        day: "Fri",
        peaks: [
          { time: "10:15", occupancyPct: 18 },
          { time: "12:00", occupancyPct: 39 },
          { time: "14:45", occupancyPct: 62 },
        ],
      },
    ];

    return raw.map((entry) => ({
      ...entry,
      peaks: [...entry.peaks].sort((a, b) => b.occupancyPct - a.occupancyPct),
    }));
  }, []);

  const yAxisTicks = Array.from({ length: 11 }, (_, i) => i * 10);
  const occupancyHeatmap = useMemo<HeatmapRow[]>(
    () => [
      {
        day: "Mon",
        values: [
          { timestamp: "9:00", occupiedDesks: 20 },
          { timestamp: "10:00", occupiedDesks: 28 },
          { timestamp: "11:00", occupiedDesks: 35 },
          { timestamp: "12:00", occupiedDesks: 42 },
          { timestamp: "13:00", occupiedDesks: 55 },
          { timestamp: "14:00", occupiedDesks: 64 },
          { timestamp: "15:00", occupiedDesks: 72 },
          { timestamp: "16:00", occupiedDesks: 67 },
          { timestamp: "17:00", occupiedDesks: 50 },
          { timestamp: "18:00", occupiedDesks: 38 },
        ],
      },
      {
        day: "Tue",
        values: [
          { timestamp: "9:00", occupiedDesks: 18 },
          { timestamp: "10:00", occupiedDesks: 30 },
          { timestamp: "11:00", occupiedDesks: 40 },
          { timestamp: "12:00", occupiedDesks: 48 },
          { timestamp: "13:00", occupiedDesks: 58 },
          { timestamp: "14:00", occupiedDesks: 66 },
          { timestamp: "15:00", occupiedDesks: 74 },
          { timestamp: "16:00", occupiedDesks: 69 },
          { timestamp: "17:00", occupiedDesks: 54 },
          { timestamp: "18:00", occupiedDesks: 36 },
        ],
      },
      {
        day: "Wed",
        values: [
          { timestamp: "9:00", occupiedDesks: 22 },
          { timestamp: "10:00", occupiedDesks: 32 },
          { timestamp: "11:00", occupiedDesks: 45 },
          { timestamp: "12:00", occupiedDesks: 52 },
          { timestamp: "13:00", occupiedDesks: 63 },
          { timestamp: "14:00", occupiedDesks: 72 },
          { timestamp: "15:00", occupiedDesks: 79 },
          { timestamp: "16:00", occupiedDesks: 70 },
          { timestamp: "17:00", occupiedDesks: 57 },
          { timestamp: "18:00", occupiedDesks: 40 },
        ],
      },
      {
        day: "Thu",
        values: [
          { timestamp: "9:00", occupiedDesks: 25 },
          { timestamp: "10:00", occupiedDesks: 36 },
          { timestamp: "11:00", occupiedDesks: 49 },
          { timestamp: "12:00", occupiedDesks: 56 },
          { timestamp: "13:00", occupiedDesks: 67 },
          { timestamp: "14:00", occupiedDesks: 76 },
          { timestamp: "15:00", occupiedDesks: 83 },
          { timestamp: "16:00", occupiedDesks: 73 },
          { timestamp: "17:00", occupiedDesks: 60 },
          { timestamp: "18:00", occupiedDesks: 44 },
        ],
      },
      {
        day: "Fri",
        values: [
          { timestamp: "9:00", occupiedDesks: 16 },
          { timestamp: "10:00", occupiedDesks: 24 },
          { timestamp: "11:00", occupiedDesks: 34 },
          { timestamp: "12:00", occupiedDesks: 41 },
          { timestamp: "13:00", occupiedDesks: 50 },
          { timestamp: "14:00", occupiedDesks: 58 },
          { timestamp: "15:00", occupiedDesks: 62 },
          { timestamp: "16:00", occupiedDesks: 55 },
          { timestamp: "17:00", occupiedDesks: 42 },
          { timestamp: "18:00", occupiedDesks: 30 },
        ],
      },
      {
        day: "Sat",
        values: [
          { timestamp: "9:00", occupiedDesks: 10 },
          { timestamp: "10:00", occupiedDesks: 14 },
          { timestamp: "11:00", occupiedDesks: 22 },
          { timestamp: "12:00", occupiedDesks: 28 },
          { timestamp: "13:00", occupiedDesks: 34 },
          { timestamp: "14:00", occupiedDesks: 40 },
          { timestamp: "15:00", occupiedDesks: 44 },
          { timestamp: "16:00", occupiedDesks: 38 },
          { timestamp: "17:00", occupiedDesks: 30 },
          { timestamp: "18:00", occupiedDesks: 20 },
        ],
      },
      {
        day: "Sun",
        values: [
          { timestamp: "9:00", occupiedDesks: 8 },
          { timestamp: "10:00", occupiedDesks: 12 },
          { timestamp: "11:00", occupiedDesks: 18 },
          { timestamp: "12:00", occupiedDesks: 24 },
          { timestamp: "13:00", occupiedDesks: 30 },
          { timestamp: "14:00", occupiedDesks: 36 },
          { timestamp: "15:00", occupiedDesks: 39 },
          { timestamp: "16:00", occupiedDesks: 33 },
          { timestamp: "17:00", occupiedDesks: 25 },
          { timestamp: "18:00", occupiedDesks: 16 },
        ],
      },
    ],
    [],
  );
  const peakFloorOptions = useMemo(
    () =>
      peakFloorOrder.map((floorID) => ({
        value: floorID,
        label: floors.find((floor) => floor.value === floorID)?.label || floorID,
      })),
    [peakFloorOrder, floors],
  );
  const activePeakFloorID = selectedPeakFloorID || peakFloorOrder[0] || "";
  const formatRangeLabel = (start: Date, end: Date) => {
    const format = (value: Date) =>
      value.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    return `${format(start)} - ${format(end)}`;
  };
  const selectedTimeRangeLabel = useMemo(
    () => formatRangeLabel(startTime, endTime),
    [startTime, endTime],
  );
  const hourlyExportRangeLabel = useMemo(
    () => formatRangeLabel(hourlyExportStartTime, hourlyExportEndTime),
    [hourlyExportStartTime, hourlyExportEndTime],
  );
  const peakExportRangeLabel = useMemo(
    () => formatRangeLabel(peakExportStartTime, peakExportEndTime),
    [peakExportStartTime, peakExportEndTime],
  );
  const sessionExportRangeLabel = useMemo(
    () => formatRangeLabel(sessionExportStartTime, sessionExportEndTime),
    [sessionExportStartTime, sessionExportEndTime],
  );
  const dowExportRangeLabel = useMemo(
    () => formatRangeLabel(dowExportStartTime, dowExportEndTime),
    [dowExportStartTime, dowExportEndTime],
  );
  const frequencyExportRangeLabel = useMemo(
    () => formatRangeLabel(frequencyExportStartTime, frequencyExportEndTime),
    [frequencyExportStartTime, frequencyExportEndTime],
  );
  const peakOccupancySource =
    activePeakFloorID && peakOccupancyByFloor[activePeakFloorID]
      ? peakOccupancyByFloor[activePeakFloorID]
      : peakOccupancyExample;
  const peakOccupancyPreview = useMemo(
    () => peakOccupancySource.slice(0, 3),
    [peakOccupancySource],
  );
  const hourlyHeatmapSource = hourlyHeatmapData ?? occupancyHeatmap;
  const occupancyHeatmapPreview = useMemo(
    () =>
      hourlyHeatmapSource.map((row) => ({
        ...row,
        values: row.values.slice(0, 4),
      })),
    [hourlyHeatmapSource],
  );
  const floorHourHeatmapPreview = useMemo(
    () =>
      SHOW_FLOOR_HOUR_HEATMAP
        ? occupancyHeatmapPreview.map((row, index) => ({
            ...row,
            day: `Floor ${index + 1}`,
          }))
        : ([] as HeatmapRow[]),
    [occupancyHeatmapPreview, SHOW_FLOOR_HOUR_HEATMAP],
  );
  const dailyFrequency = useMemo<DailyFrequencyPoint[]>(
    () => [
      { day: "Mon", usedPct: 52, usedDesks: 62 },
      { day: "Tue", usedPct: 68, usedDesks: 82 },
      { day: "Wed", usedPct: 43, usedDesks: 52 },
      { day: "Thu", usedPct: 79, usedDesks: 95 },
      { day: "Fri", usedPct: 61, usedDesks: 73 },
      { day: "Sat", usedPct: 35, usedDesks: 42 },
      { day: "Sun", usedPct: 72, usedDesks: 86 },
    ],
    [],
  );
  const dailyFrequencyPreview = useMemo(
    () => (dailyFrequencyData ?? dailyFrequency).slice(0, 5),
    [dailyFrequencyData, dailyFrequency],
  );
  const dailyFrequencySource = dailyFrequencyData ?? dailyFrequency;

  const dowComparison = useMemo<DayOfWeekPoint[]>(
    () => [
      { day: "Mon", avgUsagePct: 64 },
      { day: "Tue", avgUsagePct: 71 },
      { day: "Wed", avgUsagePct: 68 },
      { day: "Thu", avgUsagePct: 76 },
      { day: "Fri", avgUsagePct: 42 },
      { day: "Sat", avgUsagePct: 28 },
      { day: "Sun", avgUsagePct: 19 },
    ],
    [],
  );
  const dowComparisonSource = dowComparisonData ?? dowComparison;
  const dowComparisonPreview = useMemo(
    () => dowComparisonSource,
    [dowComparisonSource],
  );

  const averageSessionLength = useMemo<SessionLengthBucket[]>(
    () => [
      { label: "< 30 min", pct: 35, color: "#22C55E", midpointMinutes: 20 },
      { label: "30-60 min", pct: 25, color: "#9CD67A", midpointMinutes: 45 },
      { label: "1-2 hrs", pct: 20, color: "#F59E0B", midpointMinutes: 90 },
      { label: "2-4 hrs", pct: 12, color: "#F97316", midpointMinutes: 180 },
      { label: "4+ hrs", pct: 8, color: "#B42318", midpointMinutes: 300 },
    ],
    [],
  );
  const averageSessionPreview = useMemo(
    () => averageSessionLength,
    [averageSessionLength],
  );
  const sessionDataSource = sessionBucketsData ?? averageSessionLength;
  const sessionPreviewSource = useMemo(
    () => (sessionBucketsData ? sessionBucketsData : averageSessionPreview),
    [sessionBucketsData, averageSessionPreview],
  );

  const dailyUtilisationData = useMemo<DailyUtilisationPoint[]>(
    () => [
      { day: "Mon", utilisedPct: 72, idlePct: 28, totalDesks: 20, businessHours: 8 },
      { day: "Tue", utilisedPct: 81, idlePct: 19, totalDesks: 20, businessHours: 8 },
      { day: "Wed", utilisedPct: 68, idlePct: 32, totalDesks: 20, businessHours: 8 },
      { day: "Thu", utilisedPct: 55, idlePct: 45, totalDesks: 20, businessHours: 8 },
      { day: "Fri", utilisedPct: 43, idlePct: 57, totalDesks: 20, businessHours: 8 },
      { day: "Sat", utilisedPct: 18, idlePct: 82, totalDesks: 20, businessHours: 8 },
      { day: "Sun", utilisedPct: 9,  idlePct: 91, totalDesks: 20, businessHours: 8 },
    ],
    [],
  );

  const dailyUtilisationPreview = useMemo(
    () => dailyUtilisationData.slice(0, 5),
    [dailyUtilisationData],
  );

  const deskTurnoverData = useMemo<TurnoverDeskRow[]>(
    () => turnoverApiData ?? [],
    [turnoverApiData],
  );

  const deskTurnoverMock = useMemo<TurnoverDeskRow[]>(
    () => [
      { desk: "Desk-01", values: [{ day: "Mon", sessions: 1, occupiedMinutes: 420 }, { day: "Tue", sessions: 1, occupiedMinutes: 390 }, { day: "Wed", sessions: 2, occupiedMinutes: 400 }, { day: "Thu", sessions: 1, occupiedMinutes: 450 }, { day: "Fri", sessions: 1, occupiedMinutes: 360 }] },
      { desk: "Desk-02", values: [{ day: "Mon", sessions: 5, occupiedMinutes: 310 }, { day: "Tue", sessions: 4, occupiedMinutes: 280 }, { day: "Wed", sessions: 6, occupiedMinutes: 340 }, { day: "Thu", sessions: 5, occupiedMinutes: 300 }, { day: "Fri", sessions: 3, occupiedMinutes: 190 }] },
      { desk: "Desk-03", values: [{ day: "Mon", sessions: 0, occupiedMinutes: 0 }, { day: "Tue", sessions: 0, occupiedMinutes: 0 }, { day: "Wed", sessions: 1, occupiedMinutes: 15 }, { day: "Thu", sessions: 0, occupiedMinutes: 0 }, { day: "Fri", sessions: 0, occupiedMinutes: 0 }] },
      { desk: "Desk-04", values: [{ day: "Mon", sessions: 4, occupiedMinutes: 260 }, { day: "Tue", sessions: 5, occupiedMinutes: 300 }, { day: "Wed", sessions: 3, occupiedMinutes: 210 }, { day: "Thu", sessions: 6, occupiedMinutes: 350 }, { day: "Fri", sessions: 4, occupiedMinutes: 240 }] },
      { desk: "Desk-05", values: [{ day: "Mon", sessions: 2, occupiedMinutes: 180 }, { day: "Tue", sessions: 1, occupiedMinutes: 390 }, { day: "Wed", sessions: 2, occupiedMinutes: 200 }, { day: "Thu", sessions: 1, occupiedMinutes: 420 }, { day: "Fri", sessions: 2, occupiedMinutes: 150 }] },
      { desk: "Desk-06", values: [{ day: "Mon", sessions: 0, occupiedMinutes: 0 }, { day: "Tue", sessions: 1, occupiedMinutes: 20 }, { day: "Wed", sessions: 0, occupiedMinutes: 0 }, { day: "Thu", sessions: 0, occupiedMinutes: 0 }, { day: "Fri", sessions: 1, occupiedMinutes: 10 }] },
    ],
    [],
  );

  const deskTurnoverPreview = useMemo(
    () => (deskTurnoverData.length > 0 ? deskTurnoverData.slice(0, 6) : deskTurnoverMock),
    [deskTurnoverData, deskTurnoverMock],
  );

  const doorTrafficMock = useMemo<DoorTrafficPoint[]>(
    () => [
      { day: "Mon", in: 128, out: 112 },
      { day: "Tue", in: 141, out: 133 },
      { day: "Wed", in: 156, out: 149 },
      { day: "Thu", in: 138, out: 142 },
      { day: "Fri", in: 97, out: 105 },
    ],
    [],
  );

  const doorTrafficPreview = useMemo(
    () => ((doorTrafficData?.length ?? 0) > 0 ? doorTrafficData! : doorTrafficMock),
    [doorTrafficData, doorTrafficMock],
  );

  const getHeatColor = (value: number) => {
    const ratio = Math.max(0, Math.min(1, value / 100));
    // green -> orange -> red (shift to red earlier)
    if (ratio <= 0.4) {
      const t = ratio / 0.4;
      const r = Math.round(34 + t * (251 - 34));
      const g = Math.round(197 + t * (146 - 197));
      const b = Math.round(94 + t * (11 - 94));
      return `rgb(${r},${g},${b})`;
    }
    const t = (ratio - 0.4) / 0.6;
    const r = Math.round(251 + t * (247 - 251));
    const g = Math.round(146 + t * (59 - 146));
    const b = Math.round(11 + t * (59 - 11));
    return `rgb(${r},${g},${b})`;
  };

  const formatDeskValue = (value: number) => {
    if (!Number.isFinite(value)) return "0";
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  const getHourlyAvgDesksUsed = (point: HeatmapRow["values"][number]) => {
    if (point.avgDesksUsed !== undefined) {
      return point.avgDesksUsed;
    }

    if (point.availableDeskTime && point.totalDesks) {
      return (point.usedDeskTime || 0) / point.availableDeskTime * point.totalDesks;
    }

    return 0;
  };

  const renderOccupancyDisplayToggle = (
    value: OccupancyDisplayMode,
    onChange: (value: OccupancyDisplayMode) => void,
  ) => (
    <div
      className="w-[136px]"
      onClick={(event) => event.stopPropagation()}
    >
      <SegmentedControl
        size="xs"
        radius="md"
        data={occupancyDisplayOptions}
        value={value}
        onChange={(nextValue) => onChange(nextValue as OccupancyDisplayMode)}
        color="orange"
        fullWidth
      />
    </div>
  );

  const renderInfoIcon = (graphKey: GraphInfoKey) => (
    <button
      type="button"
      aria-label={`More information about ${graphKey} graph`}
      className="text-[#98A2B3] hover:text-[#667085] transition-colors flex items-center justify-center p-0 bg-transparent border-0 cursor-pointer"
      onClick={(event) => {
        event.stopPropagation();
        setInfoGraph(graphKey);
      }}
    >
      <BsInfoCircleFill size={15} />
    </button>
  );

  const getGraphInfoContent = (graph: GraphInfoKey) => {
    if (graph === "peak") {
      return {
        title: "Peak Occupancy by Day (Top 3)",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows the 3 busiest moments of each day — the hours when the most
              desks were in use at the same time.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: if your floor has 20 desks and 15 were occupied at 2 PM,
              that hour would show 75%.
            </p>
            <ul className="m-0 pl-5">
              <li>Each day shows up to 3 bars, ranked from busiest to third-busiest.</li>
              <li>Taller bars mean more desks were occupied at the same time.</li>
              <li>
                Hover on any bar to see the exact hour, how many desks were in
                use, and the percentage.
              </li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "session") {
      return {
        title: "Average Session Length",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows how long people typically sit at a desk before leaving,
              grouped into time ranges.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: if most sessions are under 30 minutes, people are using
              desks for quick tasks. Longer sessions suggest focused, dedicated
              work.
            </p>
            <ul className="m-0 pl-5">
              <li>The number in the centre is the overall average session time.</li>
              <li>
                Each slice of the donut is a time range (e.g. under 30 min, 1–2
                hrs) — bigger slices mean more sessions fell in that range.
              </li>
              <li>Hover on a slice to see how many sessions it includes.</li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "hourly") {
      return {
        title: "Hourly Occupancy Trend",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows how busy the floor is at each hour of the day — so you can
              see when desks are most and least in demand.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: darker boxes around 11 AM and lighter boxes after 3 PM
              mean desks were busier in the late morning and quieter in the
              afternoon.
            </p>
            <ul className="m-0 pl-5">
              <li>Each box represents one hour for the selected day.</li>
              <li>
                The percentage in each box shows how much of the available desk
                time was used during that hour. Switch to Desks to show the
                average number of desks used in each hour.
              </li>
              <li>
                Green boxes indicate lower occupancy, amber boxes show moderate
                occupancy, and red boxes highlight the busiest hours.
              </li>
              <li>
                Hover on a box to see the exact day, hour, and occupancy
                percentage.
              </li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "frequency") {
      return {
        title: "Daily Frequency Trend",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows what percentage of desks were used each day. A desk counts
              as &quot;used&quot; if anyone sat at it at least once that day.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: if 8 out of 20 desks were used on a Monday, that day
              shows 40%. A consistently low percentage could mean you have more
              desks than needed.
            </p>
            <ul className="m-0 pl-5">
              <li>Each point on the line is one day.</li>
              <li>Higher points mean more desks were used that day.</li>
              <li>
                Hover on a point to see the exact number of desks used and the
                percentage.
              </li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "dow") {
      return {
        title: "Day of Week Comparison",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows desk usage for each day of a single week (Monday to Sunday),
              so you can compare which days were busiest.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: if Friday&apos;s bar is much shorter than Tuesday&apos;s,
              fewer desks were in use that Friday — useful for planning which
              days need the most capacity.
            </p>
            <ul className="m-0 pl-5">
              <li>Each bar is one day of the selected week.</li>
              <li>Bar length shows the percentage of desk time used that day.</li>
              <li>
                Longer bars mean more desks were occupied for more of the day.
              </li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "floorHour") {
      return {
        title: "Floor x Hour Heatmap",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              A colour-coded grid that shows how busy each floor is at different
              times of day. Darker colours mean more desks were occupied during
              that hour.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: a dark cell at Floor 2 / 10 AM means that floor was
              heavily used around that time. Light or empty cells mean few or no
              desks were in use.
            </p>
            <ul className="m-0 pl-5">
              <li>Each row is a floor and each column is an hour of the day.</li>
              <li>
                Darker cells = higher occupancy, lighter cells = lower
                occupancy.
              </li>
              <li>
                Great for comparing floors side by side and finding off-peak
                hours.
              </li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "deskTurnover") {
      return {
        title: "Desk Turnover",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows how many distinct occupancy sessions each desk had per day — revealing hot-desking vs long-stay usage patterns.
            </p>
            <p className="m-0 mb-2 font-semibold">Classification uses both sessions and occupied time (weekdays only):</p>
            <ul className="m-0 pl-5 mb-3">
              <li><span className="font-semibold text-[#22C55E]">Ghost</span> — fewer than 1 session/day or less than 30 min occupied/day. Rarely used or triggered by sensor noise.</li>
              <li><span className="font-semibold text-[#FB923C]">Long-stay</span> — up to 2 sessions/day with at least 60 min occupied. Likely an assigned or permanent occupant.</li>
              <li><span className="font-semibold text-[#F73B3B]">Hot desk</span> — more than 2 sessions/day with at least 60 min occupied. Genuinely shared by multiple users.</li>
            </ul>
            <ul className="m-0 pl-5">
              <li>Each row is a desk, each column is a day.</li>
              <li>Darker cells = more sessions that day.</li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "dailyUtilisation") {
      return {
        title: "Daily Utilisation",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Shows how much of the available desk time was actually used each day.
              Unlike Daily Frequency (which only asks "was the desk touched?"), Utilisation
              measures the <strong>proportion of business hours</strong> each desk was occupied.
            </p>
            <p className="m-0 mb-3 text-[#667085] italic">
              Example: if 20 desks are available for 8 hours and total occupied time is
              64 desk-hours, utilisation is 40% — even if all desks were touched at some point.
            </p>
            <ul className="m-0 pl-5">
              <li>Each bar represents one day in the selected range.</li>
              <li>The dark segment shows utilised desk-time; the light segment shows idle (unused) desk-time.</li>
              <li>Idle time = desks were available but no one was sitting at them.</li>
              <li>Use this to size your desk estate and identify days of chronic under-use.</li>
            </ul>
          </div>
        ),
      };
    }

    if (graph === "doorTraffic") {
      return {
        title: "Door Traffic",
        body: (
          <div className="text-sm text-[#344054] leading-6">
            <p className="m-0 mb-3">
              Total number of people counted entering and leaving through the doors each day.
            </p>
            <ul className="m-0 pl-5">
              <li>Each day shows two bars — people in and people out.</li>
              <li>Hover on a bar to see the exact count.</li>
            </ul>
          </div>
        ),
      };
    }

    return {
      title: "Graph Explanation",
      body: (
        <p className="m-0 text-sm text-[#344054] leading-6">
          Explanation for this graph will be added soon.
        </p>
      ),
    };
  };

  const renderPeakCard = (data: DayPeakGroup[], expanded: boolean) => {
    const displayMode = expanded ? peakOccupancyDisplayMode : "percentage";
    const peakTotalDesks = Math.max(
      ...data.flatMap((day) =>
        day.peaks.map((peak) => Number(peak.totalDesks || peak.peakConcurrent || 0)),
      ),
      0,
    );
    const peakAxisMax = displayMode === "desks" ? Math.max(peakTotalDesks, 1) : 100;
    const peakAxisTicks =
      displayMode === "desks"
        ? Array.from({ length: 6 }, (_, index) =>
            Math.round((peakAxisMax / 5) * index),
          )
        : yAxisTicks;

    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setSpecModal(true);
            return;
          }
          setSelectedChart("peak");
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Peak Occupancy by Day (Top 3)
              </h2>
              {renderInfoIcon("peak")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Range limit up to 30 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              Which hours had the most desks occupied at the same time, ranked top
              3 per day.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {expanded && (
              <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054]">
                <span className="font-semibold">Range:</span>
                <span>{selectedTimeRangeLabel}</span>
              </div>
            )}
            {expanded && peakFloorOptions.length > 0 && (
              <div
                className="w-[220px]"
                onClick={(event) => event.stopPropagation()}
              >
                <CustomSelect
                  data={peakFloorOptions}
                  value={activePeakFloorID}
                  setValue={(value) => setSelectedPeakFloorID(value)}
                  placeholder=""
                />
              </div>
            )}
            {expanded && (
              <div
                className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  setSpecModal(true);
                }}
              >
                Edit parameters
              </div>
            )}
            {expanded && peakFloorOptions.length > 0 && (
              <div
                className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  setPeakExportModal(true);
                }}
              >
                Export csv
              </div>
            )}
            <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F73B3B]" />
              <span className="text-xs text-[#344054]">Peak value order</span>
            </div>
          </div>
        </div>

        {expanded && (
          <div className="flex flex-wrap items-center justify-end gap-2 mb-5">
            {renderOccupancyDisplayToggle(
              peakOccupancyDisplayMode,
              setPeakOccupancyDisplayMode,
            )}
            {displayMode === "desks" && peakTotalDesks > 0 && (
              <div className="flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054]">
                <span className="font-semibold">Total desks:</span>
                <span>{peakTotalDesks}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <div className="flex items-center gap-2 rounded-full border border-[#D0D5DD] px-3 py-1">
            <span className="w-2.5 h-2.5 rounded-full bg-[#F73B3B]" />
            <span className="text-xs text-[#344054]">Top 1</span>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-[#D0D5DD] px-3 py-1">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FA7070]" />
            <span className="text-xs text-[#344054]">Top 2</span>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-[#D0D5DD] px-3 py-1">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FDB5B5]" />
            <span className="text-xs text-[#344054]">Top 3</span>
          </div>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5">
          <div className="flex gap-4">
            <div className="relative w-[52px] h-[300px] text-xs text-[#667085]">
            {peakAxisTicks.map((tick, index) => {
              const tickBottom =
                displayMode === "desks" ? (tick / peakAxisMax) * 100 : tick;
              return (
                <span
                  key={`${displayMode}-${tick}-${index}`}
                  className="absolute right-1 leading-none"
                  style={{ bottom: `calc(${tickBottom}% - 6px)` }}
                >
                  {displayMode === "desks" ? tick : `${tick}%`}
                </span>
              );
            })}
          </div>

          <div
            ref={peakChartScrollRef}
            className={`flex-1 ${
              expanded
                ? "overflow-x-scroll overflow-y-hidden pb-10 cursor-grab active:cursor-grabbing"
                : "overflow-x-visible overflow-y-visible"
            } select-none`}
            onMouseDown={expanded ? handlePeakChartMouseDown : undefined}
            onMouseMove={expanded ? handlePeakChartMouseMove : undefined}
            onMouseUp={expanded ? handlePeakChartMouseUp : undefined}
            onMouseLeave={expanded ? handlePeakChartMouseUp : undefined}
          >
            <div
              className="relative h-[300px] border-l border-b border-[#D0D5DD] px-4 flex items-end justify-start gap-10 overflow-visible"
              style={{ minWidth: expanded ? Math.max(860, data.length * 130) : "auto" }}
            >
              {peakAxisTicks.map((tick, index) => {
                const tickBottom =
                  displayMode === "desks" ? (tick / peakAxisMax) * 100 : tick;
                return (
                  <div
                    key={`grid-${displayMode}-${tick}-${index}`}
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: `${tickBottom}%`,
                      height: "1px",
                      backgroundColor: "#EDF1F7",
                      zIndex: 0,
                    }}
                  />
                );
              })}

              {data.map((day) => {
                const uniqueSortedPeaks = Array.from(
                  new Set(day.peaks.map((peak) => peak.occupancyPct)),
                ).sort((a, b) => b - a);
                const allPeaksNoData =
                  day.peaks.length > 0 && day.peaks.every((peak) => peak.isNoData);

                return (
                  <div
                    key={day.day}
                    className="relative h-full flex items-end gap-2 z-10 overflow-visible"
                  >
                    {allPeaksNoData ? (
                      <div className="relative h-full w-20 sm:w-28 flex items-end justify-center overflow-visible">
                        <div className="absolute inset-x-0 text-center text-[11px] text-[#667085] whitespace-nowrap bottom-[6px]">
                          No desk occupancy
                        </div>
                      </div>
                    ) : (
                      day.peaks.map((peak) => {
                        const rankIndex = uniqueSortedPeaks.indexOf(peak.occupancyPct);
                        const peakTimeLabel = peak.isNoData ? "No data" : peak.time;
                        const peakDeskValue = Number(peak.peakConcurrent || 0);
                        const peakBarHeight =
                          displayMode === "desks"
                            ? Math.min(100, (peakDeskValue / peakAxisMax) * 100)
                            : Math.min(100, peak.occupancyPct);
                        const tooltipLabel = (
                          <div className="text-xs leading-5">
                            <div>
                              <span className="font-semibold">Day:</span> {day.day}
                            </div>
                            <div>
                              <span className="font-semibold">Hour:</span> {peakTimeLabel}
                            </div>
                            <div>
                              <span className="font-semibold">Peak occupancy:</span>{" "}
                              {peak.occupancyPct.toFixed(2)}%
                            </div>
                            {peak.peakConcurrent !== undefined && (
                              <div>
                                <span className="font-semibold">
                                  Peak concurrent desks:
                                </span>{" "}
                                {peak.peakConcurrent}
                              </div>
                            )}
                            {peak.totalDesks !== undefined && (
                              <div>
                                <span className="font-semibold">Total desks:</span>{" "}
                                {peak.totalDesks}
                              </div>
                            )}
                          </div>
                        );
                        return (
                          <div
                            key={`${day.day}-${peak.time}`}
                            className="relative h-full w-6 sm:w-8 flex items-end justify-center overflow-visible"
                          >
                            <Tooltip
                              label={tooltipLabel}
                              withArrow
                              multiline
                              classNames={{ tooltip: "max-w-[260px]" }}
                            >
                              <div
                                className={`w-5 sm:w-7 rounded-t-md shadow-sm transition-all duration-200 hover:-translate-y-1 ${
                                  rankIndex === 0
                                    ? "bg-[#F73B3B]"
                                    : rankIndex === 1
                                      ? "bg-[#FA7070]"
                                      : "bg-[#FDB5B5]"
                                }`}
                                style={{ height: `${peakBarHeight}%` }}
                              />
                            </Tooltip>
                            <div
                              className="absolute inset-x-0 text-center text-[11px] text-[#667085] whitespace-nowrap"
                              style={{ bottom: `calc(${peakBarHeight}% + 6px)` }}
                            >
                              {peakTimeLabel}
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div
                      className="absolute left-0 right-0 text-center whitespace-nowrap text-sm font-semibold text-[#344054]"
                      style={{ top: "calc(100% + 10px)" }}
                    >
                      {day.day}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  };

  const renderHeatmapCard = (data: HeatmapRow[], expanded: boolean) => {
    const displayMode = expanded ? hourlyOccupancyDisplayMode : "percentage";
    const labels = data[0]?.values.map((point) => point.timestamp) || [];
    const rowLabelWidth = expanded ? 70 : 56;
    const columns = `${rowLabelWidth}px repeat(${labels.length}, minmax(0, 1fr))`;
    const hourlyTotalDesks = Math.max(
      ...data.flatMap((row) =>
        row.values.map((point) => Number(point.totalDesks || 0)),
      ),
      0,
    );
    const hourlyFloorName =
      floors.find((floor) => floor.value === selectedHourlyFloorID)?.label || "";
    const hourlyDeskSelection =
      hourlyDesksSelection?.[selectedHourlyFloorID]?.deskOption || "";
    const selectedHourlyDesks = selectedHourlyFloorID
      ? hourlyDeskMap[selectedHourlyFloorID] || []
      : [];
    const hourlyDeskSummary = !expanded
      ? ""
      : hourlyDeskSelection === "all"
        ? `All desks selected${hourlyFloorName ? ` for ${hourlyFloorName}` : ""}`
        : selectedHourlyDesks.length > 0
          ? `${selectedHourlyDesks.length} desk${
              selectedHourlyDesks.length > 1 ? "s" : ""
            } selected`
          : "No desks selected";
    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setHourlySpecModal(true);
            return;
          }
          setSelectedChart("hourly");
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Hourly Occupancy Trend
              </h2>
              {renderInfoIcon("hourly")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Range limit up to 10 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              How busy the floor is at each hour — spot the rush hours and quiet
              periods.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap items-center justify-end gap-3">
              {expanded && (
                <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054]">
                  <span className="font-semibold">Range:</span>
                  <span>{selectedTimeRangeLabel}</span>
                </div>
              )}
              {expanded && hourlyViewMode === "days" && (
                <div
                  className="w-[220px]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <CustomSelect
                    data={floors}
                    value={selectedHourlyFloorID}
                    setValue={handleHourlyFloorChange}
                    placeholder=""
                    isLoading={isFetchingFloors}
                  />
                </div>
              )}
              {expanded && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                  onClick={(event) => {
                    event.stopPropagation();
                    setHourlySpecModal(true);
                  }}
                >
                  Edit parameters
                </div>
              )}
              {expanded && hourlyCsvRows.length > 0 && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                  onClick={(event) => {
                    event.stopPropagation();
                    setHourlyExportModal(true);
                  }}
                >
                  Export csv
                </div>
              )}
              <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
                <span
                  className="w-16 h-2.5 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, #22C55E, #FB920B, #F73B3B)",
                  }}
                />
                <span className="text-xs text-[#344054]">Low → High</span>
              </div>
            </div>
            {expanded && (
              <div
                className={`hidden sm:flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054] max-w-[420px] ${
                  selectedHourlyDesks.length > 0 ? "cursor-pointer" : "cursor-default"
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (selectedHourlyDesks.length > 0) {
                    setHourlyDesksModal(true);
                  }
                }}
              >
                <span className="font-semibold whitespace-nowrap">Selected desks:</span>
                <span className="truncate">{hourlyDeskSummary}</span>
              </div>
            )}
            {expanded && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {renderOccupancyDisplayToggle(
                  hourlyOccupancyDisplayMode,
                  setHourlyOccupancyDisplayMode,
                )}
                {displayMode === "desks" && hourlyTotalDesks > 0 && (
                  <div className="flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054]">
                    <span className="font-semibold">Total desks:</span>
                    <span>{hourlyTotalDesks}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className={`mt-[45px] rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5 ${
            expanded ? "overflow-x-auto" : "overflow-x-hidden"
          }`}
        >
          <div className={expanded ? "min-w-[760px]" : "min-w-0"}>
            <div
              className={`grid mb-2 ${expanded ? "gap-2" : "gap-1"}`}
              style={{ gridTemplateColumns: columns }}
            >
              <div />
              {labels.map((hour) => (
                <div
                  key={hour}
                  className={`text-[#667085] text-center font-medium ${
                    expanded ? "text-xs" : "text-[11px]"
                  }`}
                >
                  {hour}
                </div>
              ))}
            </div>

            <div className={`flex flex-col ${expanded ? "gap-2" : "gap-1"}`}>
              {data.map((row) => (
                <div
                  key={row.day}
                  className={`grid items-center ${expanded ? "gap-2" : "gap-1"}`}
                  style={{ gridTemplateColumns: columns }}
                >
                  <div
                    className={`font-semibold text-[#344054] ${
                      expanded ? "text-xs" : "text-[11px]"
                    }`}
                  >
                    {row.day}
                  </div>
                  {row.values.map((point) => {
                    const avgDesksUsed = getHourlyAvgDesksUsed(point);
                    const displayLabel =
                      displayMode === "desks"
                        ? formatDeskValue(avgDesksUsed)
                        : `${point.occupiedDesks.toFixed(2)}%`;

                    return (
                      <Tooltip
                        key={`${row.day}-${point.timestamp}`}
                        withArrow
                        multiline
                        label={
                          <div className="text-xs leading-5">
                            <div>
                              <span className="font-semibold">Day:</span> {row.day}
                            </div>
                            <div>
                              <span className="font-semibold">Hour:</span>{" "}
                              {point.timestamp}
                            </div>
                            {displayMode === "desks" && (
                              <div>
                                <span className="font-semibold">
                                  Avg desks used:
                                </span>{" "}
                                {formatDeskValue(avgDesksUsed)}
                              </div>
                            )}
                            <div>
                              <span className="font-semibold">Percentage:</span>{" "}
                              {point.occupiedDesks.toFixed(2)}%
                            </div>
                            {displayMode === "desks" && point.totalDesks !== undefined && (
                              <div>
                                <span className="font-semibold">Total desks:</span>{" "}
                                {point.totalDesks}
                              </div>
                            )}
                          </div>
                        }
                      >
                        <div
                          className={`rounded-md border border-white/40 flex items-center justify-center ${
                            expanded ? "h-12" : "h-11"
                          }`}
                          style={{ backgroundColor: getHeatColor(point.occupiedDesks) }}
                        >
                          {expanded && (
                            <div className="text-[13px] leading-tight font-semibold text-[#101828] text-center py-1">
                              <div>{displayLabel}</div>
                              {displayMode === "desks" && (
                                <div className="text-[10px] font-medium text-[#344054]">
                                  avg desks
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFrequencyCard = (
    data: DailyFrequencyPoint[],
    expanded: boolean,
  ) => {
    const chartWidth = expanded ? 1100 : 640;
    const chartHeight = 340;
    const padding = { top: 18, right: 20, bottom: 38, left: 42 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    const xStep = data.length > 1 ? plotWidth / (data.length - 1) : 0;
    const usedCoords = data.map((point, idx) => {
      const x = padding.left + idx * xStep;
      const y = padding.top + ((100 - point.usedPct) / 100) * plotHeight;
      return { x, y };
    });
    const buildSmoothPath = (coords: { x: number; y: number }[]) => {
      if (coords.length === 0) return "";
      if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;
      let path = `M ${coords[0].x} ${coords[0].y}`;
      for (let i = 0; i < coords.length - 1; i += 1) {
        const current = coords[i];
        const next = coords[i + 1];
        const dx = next.x - current.x;
        const c1x = current.x + dx / 3;
        const c2x = current.x + (2 * dx) / 3;
        path += ` C ${c1x} ${current.y} ${c2x} ${next.y} ${next.x} ${next.y}`;
      }
      return path;
    };

    const usedPath = buildSmoothPath(usedCoords);
    const usedAreaPath = `${usedPath} L ${
      padding.left + (data.length - 1) * xStep
    } ${padding.top + plotHeight} L ${padding.left} ${
      padding.top + plotHeight
    } Z`;
    const frequencyFloorName =
      floors.find((floor) => floor.value === selectedFrequencyFloorID)?.label || "";
    const frequencyDeskSelection =
      frequencyDesksSelection?.[selectedFrequencyFloorID]?.deskOption || "";
    const selectedFrequencyDesks = selectedFrequencyFloorID
      ? frequencyDeskMap[selectedFrequencyFloorID] || []
      : [];
    const frequencyDeskSummary = !expanded
      ? ""
      : frequencyDeskSelection === "all"
        ? `All desks selected${frequencyFloorName ? ` for ${frequencyFloorName}` : ""}`
        : selectedFrequencyDesks.length > 0
          ? `${selectedFrequencyDesks.length} desk${
              selectedFrequencyDesks.length > 1 ? "s" : ""
            } selected`
          : "No desks selected";

    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setFrequencySpecModal(true);
            return;
          }
          setSelectedChart("frequency");
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Daily Frequency Trend
              </h2>
              {renderInfoIcon("frequency")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Range limit up to 10 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              What percentage of desks got used each day — spot busy and quiet days.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-5">
              {expanded && (
                <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054] whitespace-nowrap">
                  <span className="font-semibold">Range:</span>
                  <span>{selectedTimeRangeLabel}</span>
                </div>
              )}
              {expanded && (
                <div
                  className="w-[220px]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <CustomSelect
                    data={floors}
                    value={selectedFrequencyFloorID}
                    setValue={handleFrequencyFloorChange}
                    placeholder=""
                    isLoading={isFetchingFloors}
                  />
                </div>
              )}
              {expanded && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs whitespace-nowrap"
                  onClick={(event) => {
                    event.stopPropagation();
                    setFrequencySpecModal(true);
                  }}
                >
                  Edit parameters
                </div>
              )}
              {expanded && dailyFrequencyData !== null && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs whitespace-nowrap"
                  onClick={(event) => {
                    event.stopPropagation();
                    setFrequencyExportModal(true);
                  }}
                >
                  Export csv
                </div>
              )}
              <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F73B3B]" />
                <span className="text-xs text-[#344054] whitespace-nowrap">Used desks</span>
                {expanded && showUtilisationOverlay && frequencyUtilisationOverlay && (
                  <>
                    <span className="text-[#D0D5DD]">·</span>
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: REPORTS_OVERLAY_BLUE }}
                    />
                    <span className="text-xs text-[#344054] whitespace-nowrap">Utilisation</span>
                  </>
                )}
              </div>
            </div>
            {expanded && (
              <div className="hidden sm:flex items-center gap-2">
                <div
                  className={`flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054] max-w-[420px] ${
                    selectedFrequencyDesks.length > 0 ? "cursor-pointer" : "cursor-default"
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (selectedFrequencyDesks.length > 0) {
                      setFrequencyDesksModal(true);
                    }
                  }}
                >
                  <span className="font-semibold whitespace-nowrap">Selected desks:</span>
                  <span className="truncate">{frequencyDeskSummary}</span>
                </div>
                <div
                  className="flex items-center rounded-lg border border-[#E4E7EC] overflow-hidden text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip
                    label={
                      isFetchingFreqUtilOverlay
                        ? "Loading utilisation data…"
                        : showUtilisationOverlay
                          ? "Hide utilisation from this chart"
                          : "Show utilisation on this chart"
                    }
                    withArrow
                    position="bottom"
                  >
                    <span className="inline-flex">
                      <button
                        type="button"
                        className={`px-3 py-1.5 font-medium transition-colors ${showUtilisationOverlay ? "text-white" : "bg-white text-[#667085] hover:bg-[#F9FAFB]"}`}
                        style={
                          showUtilisationOverlay
                            ? { backgroundColor: REPORTS_OVERLAY_BLUE }
                            : undefined
                        }
                        onClick={handleToggleUtilisationOverlay}
                        disabled={isFetchingFreqUtilOverlay}
                      >
                        {isFetchingFreqUtilOverlay ? "Loading…" : "+ Utilisation"}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5 overflow-x-auto">
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full h-[340px] overflow-visible"
          >
            <defs>
              <linearGradient
                id={`used-area-gradient-${expanded ? "expanded" : "preview"}`}
                x1="0" x2="0" y1="0" y2="1"
              >
                <stop offset="0%" stopColor="#F73B3B" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#F73B3B" stopOpacity="0.02" />
              </linearGradient>
              <linearGradient
                id={`util-overlay-gradient-${expanded ? "expanded" : "preview"}`}
                x1="0" x2="0" y1="0" y2="1"
              >
                <stop offset="0%" stopColor={REPORTS_OVERLAY_BLUE} stopOpacity="0.18" />
                <stop offset="100%" stopColor={REPORTS_OVERLAY_BLUE} stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {yAxisTicks.map((tick) => {
              const y = padding.top + ((100 - tick) / 100) * plotHeight;
              return (
                <g key={`freq-y-${tick}`}>
                  <line
                    x1={padding.left}
                    x2={chartWidth - padding.right}
                    y1={y}
                    y2={y}
                    stroke="#EDF1F7"
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fontWeight="500"
                    fill="#667085"
                  >
                    {tick}%
                  </text>
                </g>
              );
            })}

            <path
              d={usedAreaPath}
              fill={`url(#used-area-gradient-${expanded ? "expanded" : "preview"})`}
            />
            <path
              d={usedPath}
              fill="none"
              stroke="#F73B3B"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {showUtilisationOverlay && frequencyUtilisationOverlay && (() => {
              const overlayCoords = data.map((point, idx) => {
                const match = frequencyUtilisationOverlay.find((p) => p.day === point.day);
                if (!match) return null;
                return {
                  x: padding.left + idx * xStep,
                  y: padding.top + ((100 - match.utilisedPct) / 100) * plotHeight,
                  utilisedPct: match.utilisedPct,
                };
              }).filter(Boolean) as { x: number; y: number; utilisedPct: number }[];
              if (overlayCoords.length === 0) return null;
              const overlayPath = buildSmoothPath(overlayCoords);
              const overlayAreaPath = `${overlayPath} L ${overlayCoords[overlayCoords.length - 1].x} ${padding.top + plotHeight} L ${overlayCoords[0].x} ${padding.top + plotHeight} Z`;
              return (
                <>
                  <path d={overlayAreaPath} fill={`url(#util-overlay-gradient-${expanded ? "expanded" : "preview"})`} />
                  <path
                    d={overlayPath}
                    fill="none"
                    stroke={REPORTS_OVERLAY_BLUE}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="6 3"
                  />
                  {overlayCoords.map((coord, idx) => (
                    <Tooltip
                      key={`freq-util-overlay-${idx}`}
                      withArrow
                      label={
                        <div className="text-xs leading-5">
                          <div><span className="font-semibold">Day:</span> {data[idx]?.day}</div>
                          <div><span className="font-semibold">Utilisation:</span> {coord.utilisedPct}%</div>
                        </div>
                      }
                      position="top"
                    >
                      <circle cx={coord.x} cy={coord.y} r="4" fill={REPORTS_OVERLAY_BLUE} />
                    </Tooltip>
                  ))}
                </>
              );
            })()}

            {data.map((point, idx) => {
              const x = padding.left + idx * xStep;
              const usedY =
                padding.top + ((100 - point.usedPct) / 100) * plotHeight;
              const tooltipLabel = (
                <div className="text-xs leading-5">
                  <div>
                    <span className="font-semibold">Day:</span> {point.day}
                  </div>
                  <div>
                    <span className="font-semibold">Used %:</span>{" "}
                    {point.usedPct.toFixed(2)}%
                  </div>
                  <div>
                    <span className="font-semibold">Used desks:</span>{" "}
                    {point.usedDesks}
                  </div>
                  <div>
                    <span className="font-semibold">Total desks:</span>{" "}
                    {point.totalDesks}
                  </div>
                </div>
              );
              return (
                <g key={`freq-p-${point.day}`}>
                  <Tooltip
                    withArrow
                    multiline
                    label={tooltipLabel}
                    position="top"
                    classNames={{ tooltip: "max-w-[260px]" }}
                  >
                    <g>
                      <circle cx={x} cy={usedY} r="4" fill="#F73B3B" />
                      <circle
                        cx={x}
                        cy={usedY}
                        r="6"
                        fill="transparent"
                        stroke="#F73B3B"
                        strokeOpacity="0.18"
                        strokeWidth="2"
                      />
                    </g>
                  </Tooltip>
                  <text
                    x={x}
                    y={chartHeight - 8}
                    textAnchor="middle"
                    fontSize="13"
                    fontWeight="600"
                    fill="#344054"
                  >
                    {point.day}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  const renderFloorHourHeatmapCard = (data: HeatmapRow[]) => {
    const labels = data[0]?.values.map((point) => point.timestamp) || [];
    const rowLabelWidth = 70;
    const columns = `${rowLabelWidth}px repeat(${labels.length}, minmax(0, 1fr))`;

    return (
      <div className="rounded-2xl border border-[#E4E7EC] bg-[#F8F9FC] p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] transition-all opacity-70">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Floor x Hour Heatmap
              </h2>
              {renderInfoIcon("floorHour")}
            </div>
            <p className="text-sm text-[#667085] m-0">
              Compare how busy each floor is at different times of day.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
            <span
              className="w-16 h-2.5 rounded-full"
              style={{
                background: "linear-gradient(to right, #22C55E, #FB920B, #F73B3B)",
              }}
            />
            <span className="text-xs text-[#344054]">Low → High</span>
          </div>
        </div>

        <div className="mt-[45px] rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5 overflow-x-hidden">
          <div className="min-w-0">
            <div className="grid mb-2 gap-1" style={{ gridTemplateColumns: columns }}>
              <div />
              {labels.map((hour) => (
                <div
                  key={hour}
                  className="text-[#667085] text-center font-medium text-[11px]"
                >
                  {hour}
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-1">
              {data.map((row) => (
                <div
                  key={row.day}
                  className="grid items-center gap-1"
                  style={{ gridTemplateColumns: columns }}
                >
                  <div className="font-semibold text-[#344054] text-xs">{row.day}</div>
                  {row.values.map((point) => (
                    <div
                      key={`${row.day}-${point.timestamp}`}
                      className="rounded-md border border-white/40 flex items-center justify-center h-11"
                      style={{ backgroundColor: getHeatColor(point.occupiedDesks) }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDowCard = (data: DayOfWeekPoint[], expanded: boolean) => {
    const barHeight = expanded ? 40 : 28;
    const gap = expanded ? 18 : 14;
    const paddingLeft = 52;
    const paddingRight = 32;
    const paddingTop = 12;
    const paddingBottom = 24;
    const svgWidth = expanded ? 900 : 600;
    const chartHeight =
      paddingTop + data.length * barHeight + (data.length - 1) * gap + paddingBottom;
    const plotWidth = svgWidth - paddingLeft - paddingRight;

    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setDowSpecModal(true);
            return;
          }
          setSelectedChart("dow");
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Day of Week Comparison
              </h2>
              {renderInfoIcon("dow")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Requires exactly 7 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              Compare desk usage across the week — which days are busiest?
            </p>
          </div>
          <div className="flex items-center gap-5">
            {expanded && (
              <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054]">
                <span className="font-semibold">Range:</span>
                <span>{selectedTimeRangeLabel}</span>
              </div>
            )}
            {expanded && (
              <div className="w-[220px]" onClick={(event) => event.stopPropagation()}>
                <CustomSelect
                  data={floors}
                  value={selectedDowFloorID}
                  setValue={handleDowFloorChange}
                  placeholder=""
                  isLoading={isFetchingFloors}
                />
              </div>
            )}
            {expanded && (
              <div
                className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  setDowSpecModal(true);
                }}
              >
                Edit parameters
              </div>
            )}
            {expanded && dowComparisonData !== null && (
              <div
                className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  setDowExportModal(true);
                }}
              >
                Export csv
              </div>
            )}
            <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
              <span
                className="w-16 h-2.5 rounded-full"
                style={{
                  background: "linear-gradient(to right, #22C55E, #FB920B, #F73B3B)",
                }}
              />
              <span className="text-xs text-[#344054]">Low → High</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5">
          <svg
            viewBox={`0 0 ${svgWidth} ${chartHeight}`}
            className="w-full overflow-visible"
            style={{ height: chartHeight }}
          >
            <defs>
              <linearGradient id="dow-bar-gradient" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#F73B3B" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#F73B3B" />
              </linearGradient>
            </defs>

            {[0, 25, 50, 75, 100].map((tick) => {
              const x = paddingLeft + (tick / 100) * plotWidth;
              return (
                <g key={`dow-x-${tick}`}>
                  <line
                    x1={x}
                    x2={x}
                    y1={paddingTop}
                    y2={chartHeight - paddingBottom}
                    stroke="#EDF1F7"
                    strokeWidth="1"
                  />
                  <text
                    x={x}
                    y={chartHeight - 4}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#98A2B3"
                  >
                    {tick}%
                  </text>
                </g>
              );
            })}

            {data.map((point, idx) => {
              const y = paddingTop + idx * (barHeight + gap);
              const barWidth = (point.avgUsagePct / 100) * plotWidth;
              const isHigh = point.avgUsagePct >= 70;
              const isMed = point.avgUsagePct >= 45;
              const barColor = isHigh ? "#F73B3B" : isMed ? "#F97316" : "#22C55E";

              return (
                <g key={`dow-bar-${point.day}`}>
                  <text
                    x={paddingLeft - 8}
                    y={y + barHeight / 2 + 5}
                    textAnchor="end"
                    fontSize="13"
                    fontWeight="600"
                    fill="#344054"
                  >
                    {point.day}
                  </text>
                  <rect
                    x={paddingLeft}
                    y={y}
                    width={plotWidth}
                    height={barHeight}
                    rx="6"
                    fill="#F2F4F7"
                  />
                  <Tooltip
                    label={
                      <div className="text-xs leading-5">
                        <div>
                          <span className="font-semibold">Day:</span> {point.day}
                        </div>
                        <div>
                          <span className="font-semibold">Avg usage:</span>{" "}
                          {point.avgUsagePct}%
                        </div>
                      </div>
                    }
                    withArrow
                    withinPortal
                  >
                    <g style={{ cursor: "help" }}>
                      <rect
                        x={paddingLeft}
                        y={y}
                        width={Math.max(barWidth, 0)}
                        height={barHeight}
                        rx="6"
                        fill={barColor}
                        opacity="0.9"
                      />
                    </g>
                  </Tooltip>
                  <text
                    x={paddingLeft + barWidth + 7}
                    y={y + barHeight / 2 + 5}
                    fontSize="12"
                    fontWeight="700"
                    fill="#344054"
                  >
                    {point.avgUsagePct}%
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  const renderSessionCard = (
    data: SessionLengthBucket[],
    expanded: boolean,
    avgMinutesOverride?: number | null,
    totalDesksOverride?: number | null,
  ) => {
    const totalPct = data.reduce((sum, item) => sum + item.pct, 0) || 1;

    const polarToCartesian = (
      cx: number,
      cy: number,
      radius: number,
      angleInDegrees: number,
    ) => {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
      return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
      };
    };

    const describeArc = (
      cx: number,
      cy: number,
      radius: number,
      startAngle: number,
      endAngle: number,
    ) => {
      const start = polarToCartesian(cx, cy, radius, endAngle);
      const end = polarToCartesian(cx, cy, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

      return [
        "M",
        start.x,
        start.y,
        "A",
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
      ].join(" ");
    };

    const weightedMinutes = data.reduce(
      (sum, item) => sum + item.midpointMinutes * item.pct,
      0,
    );
    const avgMinutes =
      avgMinutesOverride !== undefined && avgMinutesOverride !== null
        ? avgMinutesOverride
        : Math.round(weightedMinutes / totalPct);
    const hours = Math.floor(avgMinutes / 60);
    const minutes = avgMinutes % 60;
    const avgLabel = `${hours}h ${String(minutes).padStart(2, "0")}m`;
    const donutSize = expanded ? 280 : 220;
    const outerRadius = donutSize / 2;
    const strokeWidth = expanded ? 56 : 48;
    const cx = outerRadius;
    const cy = outerRadius;
    const labelRadius = expanded ? donutSize / 2 + 34 : donutSize / 2 + 28;
    let runningPct = 0;
    const donutLabels = data.map((item) => {
      const slicePct = (item.pct / totalPct) * 100;
      const midPct = runningPct + slicePct / 2;
      runningPct += slicePct;
      const angle = (midPct / 100) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(angle) * labelRadius;
      const y = Math.sin(angle) * labelRadius;
      return { ...item, x, y };
    });
    const sessionFloorName =
      floors.find((floor) => floor.value === selectedSessionFloorID)?.label || "";
    const sessionDeskSelection =
      sessionDesksSelection?.[selectedSessionFloorID]?.deskOption || "";
    const selectedSessionDesks = selectedSessionFloorID
      ? sessionDeskMap[selectedSessionFloorID] || []
      : [];
    const sessionDeskSummary = !expanded
      ? ""
      : sessionDeskSelection === "all"
        ? `All desks selected${sessionFloorName ? ` for ${sessionFloorName}` : ""}`
        : selectedSessionDesks.length > 0
          ? `${selectedSessionDesks.length} desk${
              selectedSessionDesks.length > 1 ? "s" : ""
            } selected`
          : "No desks selected";

    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setSessionSpecModal(true);
            return;
          }
          setSelectedChart("session");
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Average Session Length
              </h2>
              {renderInfoIcon("session")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Range limit up to 30 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              How long people typically sit at a desk before leaving.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-5">
              {expanded && (
                <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054]">
                  <span className="font-semibold">Range:</span>
                  <span>{selectedTimeRangeLabel}</span>
                </div>
              )}
              {expanded && (
                <div
                  className="w-[220px]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <CustomSelect
                    data={floors}
                    value={selectedSessionFloorID}
                    setValue={handleSessionFloorChange}
                    placeholder=""
                    isLoading={isFetchingFloors}
                  />
                </div>
              )}
              {expanded && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSessionSpecModal(true);
                  }}
                >
                  Edit parameters
                </div>
              )}
            {expanded && sessionBucketsData !== null && (
              <div
                className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  setSessionExportModal(true);
                }}
              >
                Export csv
              </div>
            )}
              <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                <span className="text-xs text-[#344054]">Short</span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <span className="text-xs text-[#344054]">Medium</span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#B42318]" />
                <span className="text-xs text-[#344054]">Long</span>
              </div>
            </div>
            {expanded && (
              <div
                className={`hidden sm:flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054] max-w-[420px] ${
                  selectedSessionDesks.length > 0 ? "cursor-pointer" : "cursor-default"
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (selectedSessionDesks.length > 0) {
                    setSessionDesksModal(true);
                  }
                }}
              >
                <span className="font-semibold whitespace-nowrap">Selected desks:</span>
                <span className="truncate">{sessionDeskSummary}</span>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 md:p-6">
          <div className="flex flex-col items-center gap-6">
            <div
              className="relative"
              style={{
                width: expanded ? 380 : 320,
                height: expanded ? 340 : 300,
              }}
            >
              <div
                className="absolute"
                style={{
                  width: donutSize,
                  height: donutSize,
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <svg
                  width={donutSize}
                  height={donutSize}
                  viewBox={`0 0 ${donutSize} ${donutSize}`}
                >
                  {(() => {
                    let cumulative = 0;
                    return data.map((item) => {
                      const slicePct = (item.pct / totalPct) * 360;
                      const startAngle = cumulative;
                      const endAngle = cumulative + slicePct;
                      cumulative = endAngle;

                      const path = describeArc(cx, cy, outerRadius - strokeWidth / 2, startAngle, endAngle);
                      return (
                        <Tooltip
                          key={`session-slice-${item.label}`}
                          label={
                            <div className="text-xs leading-5">
                              <div>
                                <span className="font-semibold">Bucket:</span>{" "}
                                {item.label}
                              </div>
                              <div>
                                <span className="font-semibold">Percentage:</span>{" "}
                                {item.pct.toFixed(2)}%
                              </div>
                              {item.count !== undefined && (
                                <div>
                                  <span className="font-semibold">Count:</span>{" "}
                                  {item.count}
                                </div>
                              )}
                            </div>
                          }
                          withArrow
                          withinPortal
                        >
                          <g style={{ cursor: "help" }}>
                            <path
                              d={path}
                              fill="none"
                              stroke={item.color}
                              strokeWidth={strokeWidth}
                              strokeLinecap="butt"
                            />
                          </g>
                        </Tooltip>
                      );
                    });
                  })()}
                </svg>
              <div
                  className="absolute bg-white rounded-full flex flex-col items-center justify-center"
                style={{
                  inset: expanded ? "24%" : "22%",
                }}
              >
                <span className="text-3xl font-[700] text-[#101828]">
                  {avgLabel}
                </span>
                <span className="text-sm text-[#667085]">avg session</span>
                {totalDesksOverride !== undefined && totalDesksOverride !== null && (
                  <span className="text-xs text-[#98A2B3] mt-1">
                    Total desks: {totalDesksOverride}
                  </span>
                )}
              </div>
            </div>
              {donutLabels.map((item) => (
                <div
                  key={`session-label-${item.label}`}
                  className="absolute text-xs font-[600] text-[#344054] whitespace-nowrap"
                  style={{
                    left: `calc(50% + ${item.x}px)`,
                    top: `calc(50% + ${item.y}px)`,
                    transform: `translate(${item.x >= 0 ? "2px" : "-100%"}, -50%)`,
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>

            <div className="w-full flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              {data.map((item) => (
                <Tooltip
                  key={`session-${item.label}`}
                  label={
                    <div className="text-xs leading-5">
                      <div>
                        <span className="font-semibold">Bucket:</span> {item.label}
                      </div>
                      <div>
                        <span className="font-semibold">Percentage:</span>{" "}
                        {item.pct.toFixed(2)}%
                      </div>
                      {item.count !== undefined && (
                        <div>
                          <span className="font-semibold">Count:</span> {item.count}
                        </div>
                      )}
                    </div>
                  }
                  withArrow
                >
                  <div className="flex items-center gap-2 cursor-help">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-[#344054]">{item.label}</span>
                    <span className="text-sm font-[600] text-[#344054]">
                      {item.pct}%
                    </span>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDailyUtilisationCard = (
    data: DailyUtilisationPoint[],
    expanded: boolean,
  ) => {
    const avgUtilised = Math.round(
      data.reduce((sum, d) => sum + d.utilisedPct, 0) / (data.length || 1),
    );
    const bestDay = data.reduce(
      (best, d) => (d.utilisedPct > best.utilisedPct ? d : best),
      data[0] ?? { day: "-", utilisedPct: 0, idlePct: 0, totalDesks: 0, businessHours: 0 },
    );
    const worstDay = data.reduce(
      (worst, d) => (d.utilisedPct < worst.utilisedPct ? d : worst),
      data[0] ?? { day: "-", utilisedPct: 0, idlePct: 0, totalDesks: 0, businessHours: 0 },
    );
    const chartWidth = expanded ? 900 : 580;
    const chartHeight = 280;
    const padTop = 16;
    const padBottom = 36;
    const padLeft = 44;
    const padRight = 20;
    const plotW = chartWidth - padLeft - padRight;
    const plotH = chartHeight - padTop - padBottom;
    const barCount = data.length;
    const barGap = barCount > 1 ? plotW / barCount : plotW;
    const barWidth = Math.max(barGap * 0.55, 20);

    const idleColor = "#FFEDD5";

    const getUtilisedColor = (pct: number) => {
      const t = Math.max(0, Math.min(1, pct / 100));
      if (t <= 0.5) {
        const s = t / 0.5;
        const r = Math.round(253 + s * (251 - 253));
        const g = Math.round(186 + s * (146 - 186));
        const b = Math.round(116 + s * (60 - 116));
        return `rgb(${r},${g},${b})`;
      }
      const s = (t - 0.5) / 0.5;
      const r = Math.round(251 + s * (247 - 251));
      const g = Math.round(146 + s * (59 - 146));
      const b = Math.round(60 + s * (59 - 60));
      return `rgb(${r},${g},${b})`;
    };

    const tiles = [
      {
        label: "Avg Utilisation",
        value: `${avgUtilised}%`,
        sub: "across the period",
        color: getUtilisedColor(avgUtilised),
        bg: "#FFF4ED",
      },
      {
        label: "Best Day",
        value: bestDay.day,
        sub: `${bestDay.utilisedPct}% utilised`,
        color: "#16A34A",
        bg: "#F0FDF4",
      },
      {
        label: "Worst Day",
        value: worstDay.day,
        sub: `${worstDay.utilisedPct}% utilised`,
        color: "#DC2626",
        bg: "#FEF2F2",
      },
    ];

    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setUtilisationSpecModal(true);
          }
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">
                Daily Utilisation
              </h2>
              {renderInfoIcon("dailyUtilisation")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Range limit up to 10 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              How much of the available desk time was actually used each day — not just touched.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-5">
              {expanded && (
                <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054] whitespace-nowrap">
                  <span className="font-semibold">Range:</span>
                  <span>{selectedTimeRangeLabel}</span>
                </div>
              )}
              {expanded && floors.length > 0 && (
                <div
                  className="w-[220px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CustomSelect
                    data={floors}
                    value={selectedUtilisationFloorID}
                    setValue={handleUtilisationFloorChange}
                    placeholder=""
                    isLoading={isFetchingFloors}
                  />
                </div>
              )}
              {expanded && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUtilisationSpecModal(true);
                  }}
                >
                  Edit parameters
                </div>
              )}
              {expanded && (
                <div
                  className={`rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs whitespace-nowrap ${isExportingUtilisationCsv ? "opacity-50 pointer-events-none" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportUtilisationCsv();
                  }}
                >
                  {isExportingUtilisationCsv ? "Exporting…" : "Export csv"}
                </div>
              )}
              <div className="hidden sm:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FB6514]" />
                <span className="text-xs text-[#344054] whitespace-nowrap">Utilised</span>
                {expanded && showFrequencyOverlay && utilisationFrequencyOverlay && (
                  <>
                    <span className="text-[#D0D5DD]">·</span>
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: REPORTS_OVERLAY_BLUE }}
                    />
                    <span className="text-xs text-[#344054] whitespace-nowrap">Frequency</span>
                  </>
                )}
              </div>
            </div>
            {expanded && (() => {
              const utilisationFloorName = floors.find((f) => f.value === selectedUtilisationFloorID)?.label || "";
              const utilisationDeskSelection = utilisationDesksSelection?.[selectedUtilisationFloorID]?.deskOption || "";
              const selectedUtilisationDesks = selectedUtilisationFloorID ? utilisationDeskMap[selectedUtilisationFloorID] || [] : [];
              const utilisationDeskSummary = utilisationDeskSelection === "all"
                ? `All desks selected${utilisationFloorName ? ` for ${utilisationFloorName}` : ""}`
                : selectedUtilisationDesks.length > 0
                  ? `${selectedUtilisationDesks.length} desk${selectedUtilisationDesks.length > 1 ? "s" : ""} selected`
                  : "No desks selected";
              return (
                <div className="hidden sm:flex items-center gap-2">
                  <div
                    className={`flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054] max-w-[420px] ${selectedUtilisationDesks.length > 0 ? "cursor-pointer" : "cursor-default"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedUtilisationDesks.length > 0) setUtilisationDesksModal(true);
                    }}
                  >
                    <span className="font-semibold whitespace-nowrap">Selected desks:</span>
                    <span className="truncate">{utilisationDeskSummary}</span>
                  </div>
                  <div
                    className="flex items-center rounded-lg border border-[#E4E7EC] overflow-hidden text-xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      label={
                        isFetchingUtilFreqOverlay
                          ? "Loading frequency data…"
                          : showFrequencyOverlay
                            ? "Hide frequency from this chart"
                            : "Show frequency on this chart"
                      }
                      withArrow
                      position="bottom"
                    >
                      <span className="inline-flex">
                        <button
                          type="button"
                          className={`px-3 py-1.5 font-medium transition-colors ${showFrequencyOverlay ? "text-white" : "bg-white text-[#667085] hover:bg-[#F9FAFB]"}`}
                          style={
                            showFrequencyOverlay
                              ? { backgroundColor: REPORTS_OVERLAY_BLUE }
                              : undefined
                          }
                          onClick={handleToggleFrequencyOverlay}
                          disabled={isFetchingUtilFreqOverlay}
                        >
                          {isFetchingUtilFreqOverlay ? "Loading…" : "+ Frequency"}
                        </button>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>


        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5 overflow-x-auto">
          {(() => {
            const lw = expanded ? 1100 : 640;
            const lh = 340;
            const lPad = { top: 18, right: 20, bottom: 38, left: 42 };
            const lPlotW = lw - lPad.left - lPad.right;
            const lPlotH = lh - lPad.top - lPad.bottom;
            const xStep = data.length > 1 ? lPlotW / (data.length - 1) : 0;

            const utilisedCoords = data.map((p, i) => ({
              x: lPad.left + i * xStep,
              y: lPad.top + ((100 - p.utilisedPct) / 100) * lPlotH,
            }));

            const smooth = (coords: { x: number; y: number }[]) => {
              if (coords.length === 0) return "";
              if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;
              let d = `M ${coords[0].x} ${coords[0].y}`;
              for (let i = 0; i < coords.length - 1; i++) {
                const dx = coords[i + 1].x - coords[i].x;
                d += ` C ${coords[i].x + dx / 3} ${coords[i].y} ${coords[i].x + (2 * dx) / 3} ${coords[i + 1].y} ${coords[i + 1].x} ${coords[i + 1].y}`;
              }
              return d;
            };

            const utilisedPath = smooth(utilisedCoords);
            const utilisedArea = `${utilisedPath} L ${lPad.left + (data.length - 1) * xStep} ${lPad.top + lPlotH} L ${lPad.left} ${lPad.top + lPlotH} Z`;

            return (
              <svg viewBox={`0 0 ${lw} ${lh}`} className="w-full h-[340px] overflow-visible">
                <defs>
                  <linearGradient id="util-line-util-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FB6514" stopOpacity="0.22" />
                    <stop offset="100%" stopColor="#FB6514" stopOpacity="0.02" />
                  </linearGradient>
                  <linearGradient id="util-line-freq-overlay-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={REPORTS_OVERLAY_BLUE} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={REPORTS_OVERLAY_BLUE} stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((tick) => {
                  const y = lPad.top + ((100 - tick) / 100) * lPlotH;
                  return (
                    <g key={`util-l-y-${tick}`}>
                      <line x1={lPad.left} x2={lw - lPad.right} y1={y} y2={y} stroke="#EDF1F7" strokeWidth="1" />
                      <text x={lPad.left - 8} y={y + 4} textAnchor="end" fontSize="12" fontWeight="500" fill="#667085">{tick}%</text>
                    </g>
                  );
                })}
                {/* Frequency overlay area + line — rendered first so utilisation dots stay on top */}
                {showFrequencyOverlay && utilisationFrequencyOverlay && (() => {
                  const freqCoords = data.map((point, idx) => {
                    const match = utilisationFrequencyOverlay.find((p) => p.day === point.day);
                    if (!match) return null;
                    return {
                      x: lPad.left + idx * xStep,
                      y: lPad.top + ((100 - match.usedPct) / 100) * lPlotH,
                      usedPct: match.usedPct,
                      day: point.day,
                    };
                  }).filter(Boolean) as { x: number; y: number; usedPct: number; day: string }[];
                  if (freqCoords.length === 0) return null;
                  let freqPath = `M ${freqCoords[0].x} ${freqCoords[0].y}`;
                  for (let i = 0; i < freqCoords.length - 1; i++) {
                    const dx = freqCoords[i + 1].x - freqCoords[i].x;
                    freqPath += ` C ${freqCoords[i].x + dx / 3} ${freqCoords[i].y} ${freqCoords[i].x + (2 * dx) / 3} ${freqCoords[i + 1].y} ${freqCoords[i + 1].x} ${freqCoords[i + 1].y}`;
                  }
                  const freqAreaPath = `${freqPath} L ${freqCoords[freqCoords.length - 1].x} ${lPad.top + lPlotH} L ${freqCoords[0].x} ${lPad.top + lPlotH} Z`;
                  return (
                    <g key="freq-overlay-bg">
                      <path d={freqAreaPath} fill="url(#util-line-freq-overlay-grad)" />
                      <path
                        d={freqPath}
                        fill="none"
                        stroke={REPORTS_OVERLAY_BLUE}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="6 3"
                      />
                    </g>
                  );
                })()}
                {/* Utilised line area + stroke */}
                <path d={utilisedArea} fill="url(#util-line-util-grad)" />
                <path d={utilisedPath} fill="none" stroke="#FB6514" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                {/* Utilisation dots — on top of frequency area so tooltips work */}
                {data.map((point, idx) => {
                  const ux = lPad.left + idx * xStep;
                  const uy = lPad.top + ((100 - point.utilisedPct) / 100) * lPlotH;
                  return (
                    <g key={`util-l-${point.day}`}>
                      <Tooltip withArrow label={<div className="text-xs leading-5"><div><span className="font-semibold">Day:</span> {point.day}</div><div><span className="font-semibold">Utilised:</span> {point.utilisedPct}%</div></div>} position="top">
                        <g>
                          <circle cx={ux} cy={uy} r="4" fill="#FB6514" />
                          <circle cx={ux} cy={uy} r="6" fill="transparent" stroke="#FB6514" strokeOpacity="0.18" strokeWidth="2" />
                        </g>
                      </Tooltip>
                      <text x={ux} y={lh - 8} textAnchor="middle" fontSize="13" fontWeight="600" fill="#344054">{point.day}</text>
                    </g>
                  );
                })}
                {/* Frequency overlay dots — rendered last, at different y positions so they don't block utilisation dots */}
                {showFrequencyOverlay && utilisationFrequencyOverlay && (() => {
                  const freqCoords = data.map((point, idx) => {
                    const match = utilisationFrequencyOverlay.find((p) => p.day === point.day);
                    if (!match) return null;
                    return {
                      x: lPad.left + idx * xStep,
                      y: lPad.top + ((100 - match.usedPct) / 100) * lPlotH,
                      usedPct: match.usedPct,
                      day: point.day,
                    };
                  }).filter(Boolean) as { x: number; y: number; usedPct: number; day: string }[];
                  if (freqCoords.length === 0) return null;
                  return (
                    <>
                      {freqCoords.map((coord, idx) => (
                        <Tooltip
                          key={`util-freq-overlay-line-${idx}`}
                          withArrow
                          label={
                            <div className="text-xs leading-5">
                              <div><span className="font-semibold">Day:</span> {coord.day}</div>
                              <div><span className="font-semibold">Frequency:</span> {coord.usedPct}%</div>
                            </div>
                          }
                          position="top"
                        >
                          <circle cx={coord.x} cy={coord.y} r="4" fill={REPORTS_OVERLAY_BLUE} />
                        </Tooltip>
                      ))}
                    </>
                  );
                })()}
              </svg>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderDeskTurnoverCard = (data: TurnoverDeskRow[], expanded: boolean) => {
    const days = data[0]?.values.map((v) => v.day) || [];
    const maxSessions = Math.max(...data.flatMap((row) => row.values.map((v) => v.sessions)), 1);
    const deskLabelWidthCh =
      data.length > 0
        ? Math.min(Math.max(3, ...data.map((r) => r.desk.length)), 24)
        : 3;
    const turnoverBadgeMinWidth = "4.75rem";
    const turnoverYAxisColWidth = `calc(${deskLabelWidthCh}ch + ${turnoverBadgeMinWidth} + 0.5rem)`;

    const isWeekday = (day: string) => !day.startsWith("Sat") && !day.startsWith("Sun");

    // Avg weekday sessions and occupied minutes per desk (weekdays only to avoid weekend skew)
    const allAvgs = data.map((row) => {
      const wd = row.values.filter((v) => isWeekday(v.day));
      return wd.reduce((sum, v) => sum + v.sessions, 0) / (wd.length || 1);
    });
    const allAvgMinutes = data.map((row) => {
      const wd = row.values.filter((v) => isWeekday(v.day));
      return wd.reduce((sum, v) => sum + v.occupiedMinutes, 0) / (wd.length || 1);
    });

    /**
     * Dual-signal classification using both sessions (turnover) and occupied minutes.
     *
     * Ghost     — barely used: < 1 session/day OR < 30 min/day occupied.
     *             Catches sensor noise (brief triggers) and truly abandoned desks.
     *
     * Long-stay — low churn but real usage: ≤ 2 sessions/day AND ≥ 60 min/day.
     *             Typical assigned/permanent occupant who sits for long stretches.
     *
     * Hot desk  — high churn with meaningful usage: > 2 sessions/day AND ≥ 60 min/day.
     *             Genuinely shared desk with multiple distinct users per day.
     *
     * Sessions alone can't distinguish a 3×5-min sensor blip from 3 real users;
     * occupied minutes filters out that noise.
     */
    const classifyDesk = (avgSessions: number, avgMins: number) => {
      if (avgSessions < 1 || avgMins < 30)
        return { label: "Ghost", color: "#22C55E", bg: "#F0FDF4", border: "#86EFAC" };
      if (avgSessions <= 2)
        return { label: "Long-stay", color: "#FB923C", bg: "#FFF7ED", border: "#FED7AA" };
      return { label: "Hot desk", color: "#F73B3B", bg: "#FEF2F2", border: "#FECACA" };
    };

    const deskRowIndex = new Map(data.map((row, i) => [row.desk, i]));

    const getDeskClassification = (row: TurnoverDeskRow) => {
      const i = deskRowIndex.get(row.desk) ?? 0;
      return classifyDesk(allAvgs[i], allAvgMinutes[i]);
    };

    const getClassificationRank = (label: string) => {
      if (label === "Ghost") return 0;
      if (label === "Long-stay") return 1;
      return 2;
    };

    const sortedData = [...data].sort((a, b) => {
      if (turnoverDeskSortOrder === "default") {
        return a.desk.localeCompare(b.desk, undefined, { numeric: true });
      }
      const rankA = getClassificationRank(getDeskClassification(a).label);
      const rankB = getClassificationRank(getDeskClassification(b).label);
      if (rankA !== rankB) {
        return turnoverDeskSortOrder === "descending" ? rankB - rankA : rankA - rankB;
      }
      return a.desk.localeCompare(b.desk, undefined, { numeric: true });
    });

    const getTurnoverCellColor = (sessions: number) => {
      if (sessions === 0) return "#F8F9FC";
      const t = Math.min(sessions / maxSessions, 1);
      if (t <= 0.5) {
        const s = t / 0.5;
        return `rgb(${Math.round(254 + s * (251 - 254))},${Math.round(215 + s * (146 - 215))},${Math.round(170 + s * (60 - 170))})`;
      }
      const s = (t - 0.5) / 0.5;
      return `rgb(${Math.round(251 + s * (247 - 251))},${Math.round(146 + s * (59 - 146))},${Math.round(60 + s * (59 - 60))})`;
    };

    const avgTurnover = allAvgs.reduce((sum, a) => sum + a, 0) / (allAvgs.length || 1);
    const ghostCount = data.filter((_, i) => classifyDesk(allAvgs[i], allAvgMinutes[i]).label === "Ghost").length;
    const longStayCount = data.filter((_, i) => classifyDesk(allAvgs[i], allAvgMinutes[i]).label === "Long-stay").length;
    const hotDeskCount = data.filter((_, i) => classifyDesk(allAvgs[i], allAvgMinutes[i]).label === "Hot desk").length;
    const peakDay = days.length > 0
      ? days.reduce((best, day) => {
          const dayAvg = data.reduce((sum, row) => sum + (row.values.find((v) => v.day === day)?.sessions ?? 0), 0) / (data.length || 1);
          const bestAvg = data.reduce((sum, row) => sum + (row.values.find((v) => v.day === best)?.sessions ?? 0), 0) / (data.length || 1);
          return dayAvg > bestAvg ? day : best;
        }, days[0])
      : "-";

    const tiles = [
      { label: "Avg Turnover", value: `${avgTurnover.toFixed(1)}`, sub: "sessions / desk / day", color: "#FB923C", bg: "#FFF7ED" },
      { label: "Ghost Desks", value: `${ghostCount}`, sub: "< 1 session or < 30 min / day", color: "#22C55E", bg: "#F0FDF4" },
      { label: "Long-stay Desks", value: `${longStayCount}`, sub: "≤ 2 sessions & ≥ 60 min / day", color: "#FB923C", bg: "#FFF7ED" },
      { label: "Hot Desks", value: `${hotDeskCount}`, sub: "> 2 sessions & ≥ 60 min / day", color: "#F73B3B", bg: "#FEF2F2" },
    ];

    const turnoverFloorName = floors.find((f) => f.value === selectedTurnoverFloorID)?.label || "";
    const turnoverDeskSelection = turnoverDesksSelection?.[selectedTurnoverFloorID]?.deskOption || "";
    const selectedTurnoverDesks = selectedTurnoverFloorID ? turnoverDeskMap[selectedTurnoverFloorID] || [] : [];
    const turnoverDeskSummary = turnoverDeskSelection === "all"
      ? `All desks selected${turnoverFloorName ? ` for ${turnoverFloorName}` : ""}`
      : selectedTurnoverDesks.length > 0
        ? `${selectedTurnoverDesks.length} desk${selectedTurnoverDesks.length > 1 ? "s" : ""} selected`
        : "No desks selected";

    return (
      <div
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) setTurnoverSpecModal(true);
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-5 min-h-[76px]">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">Desk Turnover</h2>
              {renderInfoIcon("deskTurnover")}
              <span className="inline-flex items-center rounded-full bg-[#FFF4ED] border border-[#FEC8A3] px-2 py-0.5 text-[11px] font-medium text-[#B54708]">
                Range limit up to 10 days
              </span>
            </div>
            <p className="text-sm text-[#667085] m-0">
              How many sessions each desk had per day — spot hot desks vs long-stay occupants.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-5">
              {expanded && (
                <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054] whitespace-nowrap">
                  <span className="font-semibold">Range:</span>
                  <span>{selectedTimeRangeLabel}</span>
                </div>
              )}
              {expanded && (
                <div className="w-[220px]" onClick={(e) => e.stopPropagation()}>
                  <CustomSelect
                    data={floors}
                    value={selectedTurnoverFloorID}
                    setValue={handleTurnoverFloorChange}
                    placeholder=""
                    isLoading={isFetchingFloors}
                  />
                </div>
              )}
              {expanded && (
                <div
                  className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs whitespace-nowrap"
                  onClick={(e) => { e.stopPropagation(); setTurnoverSpecModal(true); }}
                >
                  Edit parameters
                </div>
              )}
              {expanded && (
                <div
                  className={`rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs whitespace-nowrap ${isExportingTurnoverCsv ? "opacity-50 pointer-events-none" : ""}`}
                  onClick={(e) => { e.stopPropagation(); handleExportTurnoverCsv(); }}
                >
                  {isExportingTurnoverCsv ? "Exporting…" : "Export csv"}
                </div>
              )}
              <div className="hidden sm:flex flex-col items-end gap-1.5">
                <div className="flex items-center gap-3 rounded-full bg-[#F2F4F7] px-3 py-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F8F9FC] border border-[#E4E7EC]" />
                  <span className="text-xs text-[#344054]">0 sessions</span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FB923C]" />
                  <span className="text-xs text-[#344054]">Mid</span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F73B3B]" />
                  <span className="text-xs text-[#344054]">High</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full font-semibold" style={{ color: "#F73B3B", backgroundColor: "#FEF2F2", border: "1px solid #FECACA" }}>Hot desk</span>
                  <span className="px-2 py-0.5 rounded-full font-semibold" style={{ color: "#FB923C", backgroundColor: "#FFF7ED", border: "1px solid #FED7AA" }}>Long-stay</span>
                  <span className="px-2 py-0.5 rounded-full font-semibold" style={{ color: "#22C55E", backgroundColor: "#F0FDF4", border: "1px solid #86EFAC" }}>Ghost</span>
                </div>
              </div>
            </div>
            {expanded && (
              <div className="hidden sm:flex items-center gap-2">
                <div
                  className={`flex items-center gap-2 rounded-full bg-[#F9FAFB] border border-[#EAECF0] px-3 py-1 text-xs text-[#344054] max-w-[420px] ${
                    selectedTurnoverDesks.length > 0 ? "cursor-pointer" : "cursor-default"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedTurnoverDesks.length > 0) {
                      setTurnoverDesksModal(true);
                    }
                  }}
                >
                  <span className="font-semibold whitespace-nowrap">Selected desks:</span>
                  <span className="truncate">{turnoverDeskSummary}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className="rounded-xl border border-[#EAECF0] px-4 py-3 flex flex-col gap-0.5"
              style={{ backgroundColor: tile.bg }}
            >
              <span className="text-[11px] font-medium text-[#667085] uppercase tracking-wide">{tile.label}</span>
              <span className="text-2xl font-[700] leading-tight" style={{ color: tile.color }}>{tile.value}</span>
              <span className="text-[11px] text-[#98A2B3]">{tile.sub}</span>
            </div>
          ))}
        </div>

        <div className={`rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5 overflow-x-auto ${expanded ? "max-h-[520px] overflow-y-auto" : ""}`}>
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `${turnoverYAxisColWidth} repeat(${days.length}, minmax(58px, 1fr))`,
            }}
          >
            <div
              className="flex h-10 w-full items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {expanded && data.length > 0 && (
                <Select
                  size="xs"
                  data={[
                    { value: "default", label: "Sort by" },
                    { value: "ascending", label: "Ascending" },
                    { value: "descending", label: "Descending" },
                  ]}
                  value={turnoverDeskSortOrder}
                  onChange={(val) =>
                    val && setTurnoverDeskSortOrder(val as TurnoverDeskSortOrder)
                  }
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true, position: "bottom-start" }}
                  style={{ width: "70%", minWidth: 0, marginLeft: "30px" }}
                />
              )}
            </div>
            {days.map((day) => (
              <div
                key={`to-head-${day}`}
                className="flex h-10 items-center justify-center text-xs font-[700] text-[#667085] text-center"
              >
                {day}
              </div>
            ))}
            {sortedData.map((row) => {
              const cls = getDeskClassification(row);
              return (
                <React.Fragment key={`to-row-${row.desk}`}>
                  <div className="flex h-10 w-full items-center gap-2">
                    <span
                      title={row.desk}
                      className="shrink-0 truncate text-right text-xs font-[700] text-[#344054] tabular-nums"
                      style={{ width: `${deskLabelWidthCh}ch`, minWidth: `${deskLabelWidthCh}ch` }}
                    >
                      {row.desk}
                    </span>
                    <span
                      className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-[600]"
                      style={{
                        color: cls.color,
                        backgroundColor: cls.bg,
                        border: `1px solid ${cls.border}`,
                        minWidth: turnoverBadgeMinWidth,
                      }}
                    >
                      {cls.label}
                    </span>
                  </div>
                  {days.map((day) => {
                    const cell = row.values.find((v) => v.day === day);
                    const sessions = cell?.sessions ?? 0;
                    const occupiedMins = cell?.occupiedMinutes ?? 0;
                    const tooltipLabel = (
                      <div className="text-xs leading-5">
                        <div><span className="font-semibold">Desk:</span> {row.desk}</div>
                        <div><span className="font-semibold">Day:</span> {day}</div>
                        <div><span className="font-semibold">Sessions:</span> {sessions}</div>
                        <div><span className="font-semibold">Occupied:</span> {occupiedMins} min</div>
                      </div>
                    );
                    return (
                      <Tooltip key={`to-cell-${row.desk}-${day}`} label={tooltipLabel} withArrow multiline classNames={{ tooltip: "max-w-[200px]" }}>
                        <div
                          className="h-10 rounded-md flex items-center justify-center text-[11px] font-[700] text-[#101828] cursor-default"
                          style={{ backgroundColor: getTurnoverCellColor(sessions) }}
                        >
                          {expanded && sessions > 0 ? sessions : null}
                        </div>
                      </Tooltip>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div
              className="h-2.5 rounded-full flex-1 mr-3"
              style={{ background: "linear-gradient(90deg, #FFEDD5 0%, #FB923C 55%, #F73B3B 100%)" }}
            />
            <span className="text-[11px] text-[#98A2B3]">Low → High sessions</span>
          </div>
        </div>
      </div>
    );
  };

  const renderDoorTrafficCard = (data: DoorTrafficPoint[], expanded: boolean) => {
    const maxVal = Math.max(...data.map((d) => Math.max(d.in, d.out)), 1);
    const axisMax = Math.max(Math.ceil(maxVal / 10) * 10, 10);
    const ticks = Array.from({ length: 6 }, (_, i) => Math.round((axisMax / 5) * i));

    const dayCount = data.length || 1;
    const totalIn = data.reduce((sum, d) => sum + d.in, 0);
    const totalOut = data.reduce((sum, d) => sum + d.out, 0);
    const formatAvg = (total: number) => {
      const avg = total / dayCount;
      return avg >= 100 ? `${Math.round(avg).toLocaleString()}` : `${avg.toFixed(1)}`;
    };
    const busiest = data.reduce(
      (best, d) => (d.in + d.out > best.in + best.out ? d : best),
      data[0] ?? { day: "-", in: 0, out: 0 },
    );

    const tiles = [
      { label: "Total In", value: totalIn.toLocaleString(), sub: `people in · ${dayCount} day${dayCount > 1 ? "s" : ""}`, color: "#F73B3B", bg: "#FEF2F2" },
      { label: "Total Out", value: totalOut.toLocaleString(), sub: `people out · ${dayCount} day${dayCount > 1 ? "s" : ""}`, color: "#22C55E", bg: "#F0FDF4" },
      { label: "Avg In / Day", value: formatAvg(totalIn), sub: "people per day", color: "#FB923C", bg: "#FFF7ED" },
      { label: "Avg Out / Day", value: formatAvg(totalOut), sub: `people per day · peak ${busiest.day}`, color: "#14B8A6", bg: "#F0FDFA" },
    ];

    return (
      <div
        id="door-traffic-card"
        className={`rounded-2xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(16,24,40,0.08)] cursor-pointer transition-all ${
          expanded ? "w-full" : ""
        }`}
        onClick={() => {
          if (!expanded) {
            setDoorTrafficSpecModal(true);
            return;
          }
          setSelectedChart("doorTraffic");
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-5 min-h-[76px]">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-[600] m-0 text-[#101828]">Door Traffic</h2>
              {renderInfoIcon("doorTraffic")}
            </div>
            <p className="text-sm text-[#667085] m-0">
              Total people counted in and out through the doors each day.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {expanded && (
              <div className="hidden lg:flex items-center gap-2 rounded-full bg-[#F2F4F7] px-3 py-1 text-xs text-[#344054]">
                <span className="font-semibold">Range:</span>
                <span>{selectedTimeRangeLabel}</span>
              </div>
            )}
            {expanded && (
              <div className="w-[220px]" onClick={(event) => event.stopPropagation()}>
                <CustomSelect
                  data={floors}
                  value={selectedDoorTrafficFloorID}
                  setValue={handleDoorTrafficFloorChange}
                  placeholder=""
                  isLoading={isFetchingFloors}
                />
              </div>
            )}
            {expanded && (
              <div
                className="rounded-lg h-fit uppercase border border-dashed border-grayText hover:bg-lightGray p-2 flex items-center justify-center text-grayText cursor-pointer text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  setDoorTrafficSpecModal(true);
                }}
              >
                Edit parameters
              </div>
            )}
            <div className="hidden sm:flex items-center gap-3 rounded-full bg-[#F2F4F7] px-3 py-1">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F73B3B]" />
                <span className="text-xs text-[#344054]">In</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                <span className="text-xs text-[#344054]">Out</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className="rounded-xl border border-[#EAECF0] px-4 py-3 flex flex-col gap-0.5 min-h-[88px]"
              style={{ backgroundColor: tile.bg }}
            >
              <span className="text-[11px] font-medium text-[#667085] uppercase tracking-wide">{tile.label}</span>
              <span className="text-2xl font-[700] leading-tight" style={{ color: tile.color }}>{tile.value}</span>
              <span className="text-[11px] text-[#98A2B3]">{tile.sub}</span>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 md:p-5">
          <div className="flex gap-4">
            <div className="relative w-[52px] h-[300px] text-xs text-[#667085]">
              {ticks.map((tick, i) => (
                <span
                  key={`dt-tick-${tick}-${i}`}
                  className="absolute right-1 leading-none"
                  style={{ bottom: `calc(${(tick / axisMax) * 100}% - 6px)` }}
                >
                  {tick}
                </span>
              ))}
            </div>
            <div className={`flex-1 ${expanded ? "overflow-x-auto" : "overflow-x-visible"}`}>
              <div
                className="relative h-[300px] border-l border-b border-[#D0D5DD] px-4 flex items-end justify-start gap-8"
                style={{ minWidth: expanded ? Math.max(760, data.length * 90) : "auto" }}
              >
                {ticks.map((tick, i) => (
                  <div
                    key={`dt-grid-${tick}-${i}`}
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: `${(tick / axisMax) * 100}%`,
                      height: "1px",
                      backgroundColor: "#EDF1F7",
                      zIndex: 0,
                    }}
                  />
                ))}
                {data.map((point) => (
                  <div
                    key={point.day}
                    className="relative h-full flex flex-col items-center justify-end z-10"
                  >
                    <div className="flex items-end gap-1.5 h-full">
                      <Tooltip label={`In: ${point.in}`} withArrow position="top">
                        <div
                          className="w-6 sm:w-8 rounded-t-md bg-[#F73B3B]"
                          style={{ height: `${(point.in / axisMax) * 100}%` }}
                        />
                      </Tooltip>
                      <Tooltip label={`Out: ${point.out}`} withArrow position="top">
                        <div
                          className="w-6 sm:w-8 rounded-t-md bg-[#22C55E]"
                          style={{ height: `${(point.out / axisMax) * 100}%` }}
                        />
                      </Tooltip>
                    </div>
                    <span className="absolute -bottom-6 text-[11px] font-[600] text-[#667085] whitespace-nowrap">
                      {point.day}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-6" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const graphInfo = infoGraph ? getGraphInfoContent(infoGraph) : null;

  return (
    <Container>
      <div className="flex flex-col gap-20 ">
        <div>
          <div className="flex justify-between md:items-center  flex-col md:flex-row ">
            <h1 className="text-4xl sm:text-5xl font-[500] ">Site Reports</h1>
            <p className="text-base text-grayText3 my-3 block md:hidden">
              Get a comprehensive summary of desk availability statistics for
              your chosen site
            </p>

            <div className="flex flex-col md:w-auto w-full">
              <span className=" text-sm mb-1">Site</span>
              <CustomSelect
                data={locations}
                value={locationID}
                setValue={(value) => {
                  setLocationID(value);
                }}
                placeholder={""}
                isLoading={isFetchinglocations}
                disable
              />
            </div>
          </div>
          <p className="text-base text-grayText3 my-3 hidden md:block">
            Get a comprehensive summary of desk availability statistics for your
            chosen site
          </p>
        </div>

        {!selectedChart ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderPeakCard(peakOccupancyPreview, false)}
            {renderHeatmapCard(occupancyHeatmapPreview, false)}
            {renderFrequencyCard(dailyFrequencyPreview, false)}
            {renderDailyUtilisationCard(dailyUtilisationPreview, false)}
            {renderSessionCard(
              sessionPreviewSource,
              false,
              sessionAvgMinutes,
              sessionTotalDesks,
            )}
            {renderDowCard(dowComparisonPreview, false)}
            {renderDeskTurnoverCard(deskTurnoverPreview, false)}
            {canViewDoorTraffic && renderDoorTrafficCard(doorTrafficPreview, false)}
            {SHOW_FLOOR_HOUR_HEATMAP
              ? renderFloorHourHeatmapCard(floorHourHeatmapPreview)
              : null}
          </div>
        ) : (
          <div className="flex flex-col gap-6" ref={expandedChartRef}>
            <div className="flex flex-col gap-2">
              <div className="flex justify-end">
                <Tooltip label="Close" withArrow position="left">
                  <button
                    type="button"
                    aria-label="Close expanded view"
                    onClick={handleCloseExpandedChart}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-[#667085] shadow-[0_2px_8px_rgba(16,24,40,0.14)] transition-colors hover:bg-[#EAECF0] hover:text-[#475467] cursor-pointer"
                  >
                    <IoClose size={22} aria-hidden className="shrink-0" />
                  </button>
                </Tooltip>
              </div>
              {selectedChart === "peak" &&
                renderPeakCard(peakOccupancySource, true)}
              {selectedChart === "hourly" &&
                renderHeatmapCard(hourlyHeatmapSource, true)}
              {selectedChart === "frequency" &&
                renderFrequencyCard(dailyFrequencySource, true)}
              {selectedChart === "dow" &&
                renderDowCard(dowComparisonSource, true)}
              {selectedChart === "session" &&
                renderSessionCard(
                  sessionDataSource,
                  true,
                  sessionAvgMinutes,
                  sessionTotalDesks,
                )}
              {selectedChart === "dailyUtilisation" &&
                renderDailyUtilisationCard(dailyUtilisationApiData ?? dailyUtilisationData, true)}
              {selectedChart === "deskTurnover" &&
                renderDeskTurnoverCard(deskTurnoverData, true)}
              {selectedChart === "doorTraffic" &&
                canViewDoorTraffic &&
                renderDoorTrafficCard(doorTrafficData ?? [], true)}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {selectedChart !== "peak" &&
                renderPeakCard(peakOccupancyPreview, false)}
              {selectedChart !== "hourly" &&
                renderHeatmapCard(occupancyHeatmapPreview, false)}
              {selectedChart !== "frequency" &&
                renderFrequencyCard(dailyFrequencyPreview, false)}
              {selectedChart !== "dailyUtilisation" &&
                renderDailyUtilisationCard(dailyUtilisationPreview, false)}
              {selectedChart !== "session" &&
                renderSessionCard(
                  sessionPreviewSource,
                  false,
                  sessionAvgMinutes,
                  sessionTotalDesks,
                )}
              {selectedChart !== "dow" &&
                renderDowCard(dowComparisonPreview, false)}
              {selectedChart !== "deskTurnover" &&
                renderDeskTurnoverCard(deskTurnoverPreview, false)}
              {selectedChart !== "doorTraffic" &&
                canViewDoorTraffic &&
                renderDoorTrafficCard(doorTrafficPreview, false)}
              {SHOW_FLOOR_HOUR_HEATMAP
                ? renderFloorHourHeatmapCard(floorHourHeatmapPreview)
                : null}
            </div>
          </div>
        )}
      </div>
      <Modal
        opened={Boolean(infoGraph)}
        onClose={() => setInfoGraph(null)}
        size={680}
        radius={20}
        centered
        withCloseButton={false}
        overlayProps={{ opacity: 0.35, blur: 4 }}
      >
        <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_12px_34px_rgba(16,24,40,0.12)]">
          <div className="px-6 py-5 border-b border-[#EAECF0] bg-gradient-to-r from-[#FFF7F3] to-[#FFFFFF] flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#FFE9DD] text-[#FB6514] flex items-center justify-center">
                <BsInfoCircleFill size={16} />
              </div>
              <div>
                <h3 className="m-0 text-lg font-semibold text-[#101828]">
                  {graphInfo?.title}
                </h3>
              </div>
            </div>
          </div>
          <div className="px-6 py-5 bg-white">
            <div className="rounded-xl border border-[#EAECF0] bg-white px-4 py-4">
              {graphInfo?.body}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-[#EAECF0] bg-white flex justify-end">
            <Button
              color="#FB6514"
              size="md"
              onClick={() => setInfoGraph(null)}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={specModal}
        onClose={() => setSpecModal(false)}
        size={700}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Peak Occupancy Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floors</span>
            <MultiSelect
              data={floors}
              value={selectedFloorIDs}
              onChange={setSelectedFloorIDs}
              withScrollArea={false}
              searchable
              clearable
              nothingFoundMessage={
                locationID
                  ? "No floors found for this site"
                  : "Select a site first"
              }
              styles={{
                dropdown: {
                  maxHeight: 180,
                  overflowY: "auto",
                  borderColor: "#BBC3CF",
                },
                input: { borderRadius: "8px", borderColor: "#BBC3CF" },
              }}
              disabled={!locationID}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !locationID || selectedFloorIDs.length === 0 || error !== null
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyPeakOccupancyParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={sessionSpecModal}
        onClose={() => setSessionSpecModal(false)}
        size={860}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Average Session Length Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floor</span>
            <CustomSelect
              data={floors}
              value={selectedSessionFloorID}
              setValue={(value) => setSelectedSessionFloorID(value)}
              placeholder=""
              isLoading={isFetchingFloors}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          {selectedSessionFloorID &&
            sessionDesksSelection?.[selectedSessionFloorID] && (
              <DeskOption
                key={selectedSessionFloorID}
                floorID={selectedSessionFloorID}
                floorName={
                  floors.find((f) => f.value === selectedSessionFloorID)?.label ||
                  selectedSessionFloorID
                }
                selection={sessionDesksSelection[selectedSessionFloorID]}
                desksSelection={sessionDesksSelection}
                setDeskSelection={setSessionDeskSelection}
                deskMap={sessionDeskMap}
                setDeskMap={setSessionDeskMap}
                deskRange={sessionDeskRange}
                setDeskRange={setSessionDeskRange}
              />
            )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !locationID ||
                !selectedSessionFloorID ||
                error !== null ||
                (sessionDesksSelection?.[selectedSessionFloorID]?.deskOption || "") ===
                  ""
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplySessionParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={dowSpecModal}
        onClose={() => setDowSpecModal(false)}
        size={860}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Day-of-Week Comparison Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floor</span>
            <CustomSelect
              data={floors}
              value={selectedDowFloorID}
              setValue={(value) => setSelectedDowFloorID(value)}
              placeholder=""
              isLoading={isFetchingFloors}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          {selectedDowFloorID && dowDesksSelection?.[selectedDowFloorID] && (
            <DeskOption
              key={selectedDowFloorID}
              floorID={selectedDowFloorID}
              floorName={
                floors.find((f) => f.value === selectedDowFloorID)?.label ||
                selectedDowFloorID
              }
              selection={dowDesksSelection[selectedDowFloorID]}
              desksSelection={dowDesksSelection}
              setDeskSelection={setDowDeskSelection}
              deskMap={dowDeskMap}
              setDeskMap={setDowDeskMap}
              deskRange={dowDeskRange}
              setDeskRange={setDowDeskRange}
            />
          )}

          {dowRequestError && <div className="text-red-500 text-sm">{dowRequestError}</div>}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !locationID ||
                !selectedDowFloorID ||
                error !== null ||
                (dowDesksSelection?.[selectedDowFloorID]?.deskOption || "") === ""
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyDowParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={hourlySpecModal}
        onClose={() => setHourlySpecModal(false)}
        size={920}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-2xl font-medium">Hourly Occupancy Parameters</p>
            <SegmentedControl
              value={hourlyViewMode}
              onChange={(v) => handleHourlyViewModeChange(v as "days" | "floors")}
              data={[
                { label: "By Days", value: "days" },
                { label: "By Floors", value: "floors" },
              ]}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">
              {hourlyViewMode === "floors" ? "Floors" : "Floor"}
            </span>
            {hourlyViewMode === "floors" ? (
              <MultiSelect
                data={floors}
                value={selectedHourlyFloorIDs}
                onChange={setSelectedHourlyFloorIDs}
                searchable
                clearable
                placeholder="Select floors"
                nothingFoundMessage={locationID ? "No floors found" : "Select a site first"}
                disabled={isFetchingFloors}
                styles={{ dropdown: { maxHeight: 200, overflowY: "auto" } }}
              />
            ) : (
              <CustomSelect
                data={floors}
                value={selectedHourlyFloorID}
                setValue={(value) => setSelectedHourlyFloorID(value)}
                placeholder=""
                isLoading={isFetchingFloors}
              />
            )}
          </div>

          {hourlyViewMode === "floors" ? (
            <div className="flex flex-col gap-1 w-full">
              <span className="m-0 text-base font-medium">Date & hours</span>
              <DatePickerInput
                value={startTime}
                onChange={handleFloorsModeDateChange}
                placeholder="Pick date"
              />
              <div className="flex gap-4">
                <div className="flex flex-col gap-1 flex-1">
                  <span className="text-sm text-[#667085]">Start hour</span>
                  <TimeInput
                    value={startTime.toTimeString().slice(0, 5)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleStartHourChange(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <span className="text-sm text-[#667085]">End hour</span>
                  <TimeInput
                    value={endTime.toTimeString().slice(0, 5)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEndHourChange(e.target.value)}
                  />
                </div>
              </div>
              {error && <div className="text-red-500">{error}</div>}
            </div>
          ) : (
            <div className="flex gap-6 w-full">
              <div className="flex flex-col gap-1 w-full">
                <div className="flex gap-2 items-center mb-2">
                  <span className="m-0 text-base font-medium">Start date & hour</span>
                </div>
                <DatePickerInput
                  value={startTime}
                  onChange={handleStartDateChange}
                  placeholder="Pick start date"
                />
                <TimeInput
                  value={startTime.toTimeString().slice(0, 5)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleStartHourChange(event.target.value);
                  }}
                  placeholder="Pick start time"
                />
              </div>

              <div className="flex flex-col gap-1 w-full">
                <div className="flex gap-2 items-center mb-2">
                  <span className="m-0 text-base font-medium">End date & hour</span>
                </div>
                <DatePickerInput
                  value={endTime}
                  onChange={handleEndDateChange}
                  placeholder="Pick end date"
                />
                <TimeInput
                  value={endTime.toTimeString().slice(0, 5)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleEndHourChange(event.target.value);
                  }}
                  placeholder="Pick end time"
                />
                {error && <div className="text-red-500">{error}</div>}
              </div>
            </div>
          )}

          {hourlyViewMode === "floors"
            ? selectedHourlyFloorIDs.map((fid) =>
                hourlyDesksSelection?.[fid] ? (
                  <DeskOption
                    key={fid}
                    floorID={fid}
                    floorName={floors.find((f) => f.value === fid)?.label || fid}
                    selection={hourlyDesksSelection[fid]}
                    desksSelection={hourlyDesksSelection}
                    setDeskSelection={setHourlyDeskSelection}
                    deskMap={hourlyDeskMap}
                    setDeskMap={setHourlyDeskMap}
                    deskRange={hourlyDeskRange}
                    setDeskRange={setHourlyDeskRange}
                  />
                ) : null
              )
            : selectedHourlyFloorID && hourlyDesksSelection?.[selectedHourlyFloorID] && (
                <DeskOption
                  key={selectedHourlyFloorID}
                  floorID={selectedHourlyFloorID}
                  floorName={
                    floors.find((f) => f.value === selectedHourlyFloorID)?.label ||
                    selectedHourlyFloorID
                  }
                  selection={hourlyDesksSelection[selectedHourlyFloorID]}
                  desksSelection={hourlyDesksSelection}
                  setDeskSelection={setHourlyDeskSelection}
                  deskMap={hourlyDeskMap}
                  setDeskMap={setHourlyDeskMap}
                  deskRange={hourlyDeskRange}
                  setDeskRange={setHourlyDeskRange}
                />
              )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !locationID ||
                error !== null ||
                (hourlyViewMode === "floors"
                  ? selectedHourlyFloorIDs.length === 0 ||
                    selectedHourlyFloorIDs.some(
                      (fid) => (hourlyDesksSelection?.[fid]?.deskOption || "") === ""
                    )
                  : !selectedHourlyFloorID ||
                    (hourlyDesksSelection?.[selectedHourlyFloorID]?.deskOption || "") === "")
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyHourlyParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={utilisationSpecModal}
        onClose={() => setUtilisationSpecModal(false)}
        size={920}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Daily Utilisation Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floor</span>
            <CustomSelect
              data={floors}
              value={selectedUtilisationFloorID}
              setValue={(value) => setSelectedUtilisationFloorID(value)}
              placeholder=""
              isLoading={isFetchingFloors}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          {selectedUtilisationFloorID && utilisationDesksSelection?.[selectedUtilisationFloorID] && (
            <DeskOption
              key={selectedUtilisationFloorID}
              floorID={selectedUtilisationFloorID}
              floorName={
                floors.find((f) => f.value === selectedUtilisationFloorID)?.label ||
                selectedUtilisationFloorID
              }
              selection={utilisationDesksSelection[selectedUtilisationFloorID]}
              desksSelection={utilisationDesksSelection}
              setDeskSelection={setUtilisationDeskSelection}
              deskMap={utilisationDeskMap}
              setDeskMap={setUtilisationDeskMap}
              deskRange={utilisationDeskRange}
              setDeskRange={setUtilisationDeskRange}
            />
          )}

          {utilisationRequestError && (
            <div className="text-red-500 text-sm">{utilisationRequestError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !selectedUtilisationFloorID ||
                !locationID ||
                error !== null ||
                (utilisationDesksSelection?.[selectedUtilisationFloorID]?.deskOption || "") === ""
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyUtilisationParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={dowExportModal}
        onClose={() => setDowExportModal(false)}
        size={760}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Export Day-of-Week CSV</p>
          </div>

          <div className="rounded-full bg-[#F2F4F7] px-3 py-2 text-xs text-[#344054] w-fit">
            <span className="font-semibold mr-1">Selected range:</span>
            <span>{dowExportRangeLabel}</span>
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={dowExportStartTime}
                onChange={handleDowExportStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={dowExportStartTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleDowExportStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={dowExportEndTime}
                onChange={handleDowExportEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={dowExportEndTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleDowExportEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {dowExportError && <div className="text-red-500">{dowExportError}</div>}
            </div>
          </div>

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={!selectedDowFloorID || dowExportError !== null}
              loading={isExportingDowCsv}
              onClick={handleExportDowCsv}
            >
              Export csv
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={hourlyExportModal}
        onClose={() => setHourlyExportModal(false)}
        size={760}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Export Hourly Occupancy CSV</p>
          </div>

          <div className="rounded-full bg-[#F2F4F7] px-3 py-2 text-xs text-[#344054] w-fit">
            <span className="font-semibold mr-1">Selected range:</span>
            <span>{hourlyExportRangeLabel}</span>
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={hourlyExportStartTime}
                onChange={handleHourlyExportStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={hourlyExportStartTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleHourlyExportStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={hourlyExportEndTime}
                onChange={handleHourlyExportEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={hourlyExportEndTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleHourlyExportEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
            </div>
          </div>

          {hourlyExportError && (
            <div className="text-red-500 text-sm">{hourlyExportError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={!selectedHourlyFloorID || hourlyExportError !== null}
              loading={isExportingHourlyCsv}
              onClick={handleExportHourlyCsv}
            >
              Export csv
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={peakExportModal}
        onClose={() => setPeakExportModal(false)}
        size={760}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Export Peak Occupancy CSV</p>
          </div>

          <div className="rounded-full bg-[#F2F4F7] px-3 py-2 text-xs text-[#344054] w-fit">
            <span className="font-semibold mr-1">Selected range:</span>
            <span>{peakExportRangeLabel}</span>
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={peakExportStartTime}
                onChange={handlePeakExportStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={peakExportStartTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handlePeakExportStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={peakExportEndTime}
                onChange={handlePeakExportEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={peakExportEndTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handlePeakExportEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
            </div>
          </div>

          {peakExportError && <div className="text-red-500 text-sm">{peakExportError}</div>}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={!activePeakFloorID || peakExportError !== null}
              loading={isExportingPeakCsv}
              onClick={handleExportPeakCsv}
            >
              Export csv
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={sessionExportModal}
        onClose={() => setSessionExportModal(false)}
        size={760}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Export Average Session CSV</p>
          </div>

          <div className="rounded-full bg-[#F2F4F7] px-3 py-2 text-xs text-[#344054] w-fit">
            <span className="font-semibold mr-1">Selected range:</span>
            <span>{sessionExportRangeLabel}</span>
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={sessionExportStartTime}
                onChange={handleSessionExportStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={sessionExportStartTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleSessionExportStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={sessionExportEndTime}
                onChange={handleSessionExportEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={sessionExportEndTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleSessionExportEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
            </div>
          </div>

          {sessionExportError && (
            <div className="text-red-500 text-sm">{sessionExportError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={!selectedSessionFloorID || sessionExportError !== null}
              loading={isExportingSessionCsv}
              onClick={handleExportSessionCsv}
            >
              Export csv
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={frequencyExportModal}
        onClose={() => setFrequencyExportModal(false)}
        size={760}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Export Daily Frequency CSV</p>
          </div>

          <div className="rounded-full bg-[#F2F4F7] px-3 py-2 text-xs text-[#344054] w-fit">
            <span className="font-semibold mr-1">Selected range:</span>
            <span>{frequencyExportRangeLabel}</span>
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={frequencyExportStartTime}
                onChange={handleFrequencyExportStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={frequencyExportStartTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleFrequencyExportStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={frequencyExportEndTime}
                onChange={handleFrequencyExportEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={frequencyExportEndTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleFrequencyExportEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
            </div>
          </div>

          {frequencyExportError && (
            <div className="text-red-500 text-sm">{frequencyExportError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={!selectedFrequencyFloorID || frequencyExportError !== null}
              loading={isExportingFrequencyCsv}
              onClick={handleExportDailyFrequencyCsv}
            >
              Export csv
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={frequencySpecModal}
        onClose={() => setFrequencySpecModal(false)}
        size={920}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Daily Frequency Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floor</span>
            <CustomSelect
              data={floors}
              value={selectedFrequencyFloorID}
              setValue={(value) => setSelectedFrequencyFloorID(value)}
              placeholder=""
              isLoading={isFetchingFloors}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>

            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          {selectedFrequencyFloorID &&
            frequencyDesksSelection?.[selectedFrequencyFloorID] && (
              <DeskOption
                key={selectedFrequencyFloorID}
                floorID={selectedFrequencyFloorID}
                floorName={
                  floors.find((f) => f.value === selectedFrequencyFloorID)?.label ||
                  selectedFrequencyFloorID
                }
                selection={frequencyDesksSelection[selectedFrequencyFloorID]}
                desksSelection={frequencyDesksSelection}
                setDeskSelection={setFrequencyDeskSelection}
                deskMap={frequencyDeskMap}
                setDeskMap={setFrequencyDeskMap}
                deskRange={frequencyDeskRange}
                setDeskRange={setFrequencyDeskRange}
              />
            )}

          {frequencyRequestError && (
            <div className="text-red-500 text-sm">{frequencyRequestError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !selectedFrequencyFloorID ||
                !locationID ||
                error !== null ||
                (frequencyDesksSelection?.[selectedFrequencyFloorID]?.deskOption ||
                  "") === ""
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyDailyFrequencyParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={frequencyDesksModal}
        onClose={() => setFrequencyDesksModal(false)}
        centered
        title="Selected desks"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#667085] m-0">
            {floors.find((f) => f.value === selectedFrequencyFloorID)?.label ||
              selectedFrequencyFloorID}
          </p>
          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#EAECF0] bg-white p-3">
            {(frequencyDeskMap[selectedFrequencyFloorID] || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(frequencyDeskMap[selectedFrequencyFloorID] || []).map((deskID) => (
                  <span
                    key={deskID}
                    className="rounded-md bg-[#F2F4F7] px-2 py-1 text-xs text-[#344054]"
                  >
                    {deskID}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#667085] m-0">No desks selected.</p>
            )}
          </div>
        </div>
      </Modal>
      <Modal
        opened={turnoverSpecModal}
        onClose={() => setTurnoverSpecModal(false)}
        size={920}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Desk Turnover Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floor</span>
            <CustomSelect
              data={floors}
              value={selectedTurnoverFloorID}
              setValue={(value) => setSelectedTurnoverFloorID(value)}
              placeholder=""
              isLoading={isFetchingFloors}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          {selectedTurnoverFloorID &&
            turnoverDesksSelection?.[selectedTurnoverFloorID] && (
              <DeskOption
                key={selectedTurnoverFloorID}
                floorID={selectedTurnoverFloorID}
                floorName={
                  floors.find((f) => f.value === selectedTurnoverFloorID)?.label ||
                  selectedTurnoverFloorID
                }
                selection={turnoverDesksSelection[selectedTurnoverFloorID]}
                desksSelection={turnoverDesksSelection}
                setDeskSelection={setTurnoverDeskSelection}
                deskMap={turnoverDeskMap}
                setDeskMap={setTurnoverDeskMap}
                deskRange={turnoverDeskRange}
                setDeskRange={setTurnoverDeskRange}
              />
            )}

          {turnoverRequestError && (
            <div className="text-red-500 text-sm">{turnoverRequestError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={
                !selectedTurnoverFloorID ||
                !locationID ||
                error !== null ||
                (turnoverDesksSelection?.[selectedTurnoverFloorID]?.deskOption || "") === ""
              }
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyTurnoverParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={doorTrafficSpecModal}
        onClose={() => setDoorTrafficSpecModal(false)}
        size={920}
        radius={20}
      >
        <div className="p-3 flex flex-col gap-6">
          <div className="flex gap-2 items-center mb-2">
            <p className="text-2xl font-medium">Door Traffic Parameters</p>
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Site</span>
            <CustomSelect
              data={locations}
              value={locationID}
              setValue={(value) => setLocationID(value)}
              placeholder=""
              isLoading={isFetchinglocations}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-medium mb-1">Floor</span>
            <CustomSelect
              data={floors}
              value={selectedDoorTrafficFloorID}
              setValue={(value) => setSelectedDoorTrafficFloorID(value)}
              placeholder=""
              isLoading={isFetchingFloors}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">Start date & hour</span>
              </div>
              <DatePickerInput
                value={startTime}
                onChange={handleStartDateChange}
                placeholder="Pick start date"
              />
              <TimeInput
                value={startTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleStartHourChange(event.target.value);
                }}
                placeholder="Pick start time"
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <div className="flex gap-2 items-center mb-2">
                <span className="m-0 text-base font-medium">End date & hour</span>
              </div>
              <DatePickerInput
                value={endTime}
                onChange={handleEndDateChange}
                placeholder="Pick end date"
              />
              <TimeInput
                value={endTime.toTimeString().slice(0, 5)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleEndHourChange(event.target.value);
                }}
                placeholder="Pick end time"
              />
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </div>

          {doorTrafficRequestError && (
            <div className="text-red-500 text-sm">{doorTrafficRequestError}</div>
          )}

          <div className="flex gap-4 justify-end">
            <Button
              color="#FB6514"
              size="md"
              disabled={!selectedDoorTrafficFloorID || !locationID || error !== null}
              loading={isFetchingFloors || isFetchingPeakOccupancy}
              onClick={handleApplyDoorTrafficParams}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        opened={turnoverDesksModal}
        onClose={() => setTurnoverDesksModal(false)}
        centered
        title="Selected desks"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#667085] m-0">
            {floors.find((f) => f.value === selectedTurnoverFloorID)?.label || selectedTurnoverFloorID}
          </p>
          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#EAECF0] bg-white p-3">
            {(turnoverDeskMap[selectedTurnoverFloorID] || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(turnoverDeskMap[selectedTurnoverFloorID] || []).map((deskID) => (
                  <span key={deskID} className="rounded-md bg-[#F2F4F7] px-2 py-1 text-xs text-[#344054]">
                    {deskID}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#667085] m-0">No desks selected.</p>
            )}
          </div>
        </div>
      </Modal>
      <Modal
        opened={utilisationDesksModal}
        onClose={() => setUtilisationDesksModal(false)}
        centered
        title="Selected desks"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#667085] m-0">
            {floors.find((f) => f.value === selectedUtilisationFloorID)?.label || selectedUtilisationFloorID}
          </p>
          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#EAECF0] bg-white p-3">
            {(utilisationDeskMap[selectedUtilisationFloorID] || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(utilisationDeskMap[selectedUtilisationFloorID] || []).map((deskID) => (
                  <span key={deskID} className="rounded-md bg-[#F2F4F7] px-2 py-1 text-xs text-[#344054]">
                    {deskID}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#667085] m-0">No desks selected.</p>
            )}
          </div>
        </div>
      </Modal>
      <Modal
        opened={hourlyDesksModal}
        onClose={() => setHourlyDesksModal(false)}
        centered
        title="Selected desks"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#667085] m-0">
            {floors.find((f) => f.value === selectedHourlyFloorID)?.label ||
              selectedHourlyFloorID}
          </p>
          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#EAECF0] bg-white p-3">
            {(hourlyDeskMap[selectedHourlyFloorID] || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(hourlyDeskMap[selectedHourlyFloorID] || []).map((deskID) => (
                  <span
                    key={deskID}
                    className="rounded-md bg-[#F2F4F7] px-2 py-1 text-xs text-[#344054]"
                  >
                    {deskID}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#667085] m-0">No desks selected.</p>
            )}
          </div>
        </div>
      </Modal>
      <Modal
        opened={sessionDesksModal}
        onClose={() => setSessionDesksModal(false)}
        centered
        title="Selected desks"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#667085] m-0">
            {floors.find((f) => f.value === selectedSessionFloorID)?.label ||
              selectedSessionFloorID}
          </p>
          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#EAECF0] bg-white p-3">
            {(sessionDeskMap[selectedSessionFloorID] || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(sessionDeskMap[selectedSessionFloorID] || []).map((deskID) => (
                  <span
                    key={deskID}
                    className="rounded-md bg-[#F2F4F7] px-2 py-1 text-xs text-[#344054]"
                  >
                    {deskID}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#667085] m-0">No desks selected.</p>
            )}
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default ReportsNew;
